import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import type {
  BidderAnomaliesResponse,
  BidderOperationalAnomaliesResponse,
  BidderConfidence,
  BidderDataQuality,
  BidderDirection,
  BidderEffectivenessEvent,
  BidderEffectivenessFilters,
  BidderEffectivenessSummary,
  BidderEventsResponse,
  BidderOutcome,
  BidderRulesResponse,
  BidderTimelineResponse,
} from "../components/MonitoringPage/BidderEffectiveness/bidderEffectivenessTypes";
import type {
  MonitoringBidderOverviewResponse,
  MonitoringPosition51Replay,
  MonitoringPosition51Summary,
} from "../components/MonitoringPage/types";
import {
  Account,
  DashboardSummary,
  ChartData,
  TopModelData,
  CampaignMetrics,
  AccountPerformanceData,
  SummaryManualFields,
  SummaryTableData,
  SummaryTaskInfo,
  SummaryBatchStartItem,
  SavedSummaryListItem,
  SummaryShareItem,
  CreateSummaryShareResponse,
  AIQueryRequest,
  AIQueryResponse,
  SummaryAssistantRequest,
  SummaryAssistantResponse,
  SummaryAssistantSettings,
  SummaryManualHighlightColor,
  SummaryManualHighlightColumnKey,
  Agent,
  AgentThreadSummary,
  AgentThreadDetail,
  OpenClawThreadSummary,
  OpenClawThreadDetail,
  OpenClawThreadCreateRequest,
  OpenClawThreadMessageRequest,
  OpenCodeMessageJobStartResponse,
  OpenCodeMessageJobStatusResponse,
  OpenCodeRunSummary,
  OpenCodeCancelRunResponse,
  OpenClawRuntimeRunsResponse,
  OpenClawRuntimeCleanupResponse,
  OpenCodePingResponse,
  OpenCodeModelPresetsResponse,
  OpenCodeModelPresetsUpdateRequest,
  OpenClawMemoryResponse,
  OpenClawMemoryUpdateRequest,
  AITableAnalysisRequest,
  Review,
  AIReviewDraft,
  AIReviewDraftSource,
  ChatData,
  GetChatByReviewRequest,
  AccountRating,
  AutoloadReportDetails,
  AutoloadReportItem,
  AutoloadReportsResponse,
  RealtimeSummary,
  SubscriptionRefreshResponse,
  RealtimeXlMetricsResponse,
  AdvertisementsTableResponse,
  SyncResponse,
  FileInfoResponse,
  OpenClawAgentRequest,
  OpenClawAgentResponse,
  OpenClawSkillsListRequest,
  OpenClawSkillsListResponse,
  OpenClawSkillToggleRequest,
  OpenClawSkillToggleResponse,
  OpenClawSystemStatusRequest,
  OpenClawSystemStatusResponse,
  OpenClawSystemLogsRequest,
  OpenClawSystemLogsResponse,
  OpenClawSystemRestartRequest,
  OpenClawSystemRestartResponse,
  OpenClawModelsListRequest,
  OpenClawModelsListResponse,
  OpenClawModelsSetRequest,
  OpenClawModelsSetResponse,
  ClawHubSearchRequest,
  ClawHubSearchResponse,
  ClawHubInstallRequest,
  ClawHubInstallResponse,
  ServiceLevelRunRequest,
  ServiceLevelRunResponse,
  TaskManagerWorkspace,
  TaskManagerTask,
  TaskManagerAIAnalysis,
  TaskManagerComment,
  TaskManagerChecklist,
  TaskManagerChecklistItem,
  TaskManagerTag,
  TaskManagerActivityLog,
  TaskAnalyzeRequest,
  TaskAssistantBootstrapResponse,
  ServiceLevelAccountLoginSetting,
  ServiceLevelAccountLoginSettingsUpsertRequest,
  ServiceLevelProfileGroupBindingsResponse,
  ServiceLevelProfileGroupBindingsUpdateRequest,
  ServiceLevelSourceAccount,
  ServiceLevelTask,
  ServiceLevelUploadRequest,
  ServiceLevelUploadResponse,
  ScheduleSettings,
  ScheduleSettingsResponse,
  ServiceLevelHistoryResponse,
  ServiceLevelHistoryWriteRequest,
  ServiceLevelHistoryWriteResponse,
  FeedbackSettings,
} from "../types/api";
import {
  clearStoredAuthSession,
  shouldForceLogoutOn401,
} from "../utils/authSession";
import type {
  AnalyticsModelsResponse,
  AnalyticsTimelineResponse,
} from "../components/MonitoringPage/types/analyticsTypes";
import type {
  MonitoringCpxAccountSummary,
  MonitoringActionHistorySummaryResponse,
  MonitoringActionHistoryTodaySummaryResponse,
  MonitoringLatestActionResponse,
  MonitoringCpxSnapshotResponse,
  MonitoringPlanningPricesIn,
  MonitoringPlanningPricesOut,
  XLRotationTriggerResponse,
  XLRotationDetail,
  XLRotationRollbackResponse,
  XLRotationRestartResponse,
  XLRotationRestartRequest,
  XLRotationListItem,
} from "../components/MonitoringPage/types";

export type BalanceCacheEntry = {
  stale: boolean;
  is_stale?: boolean;
  stale_reason: string | null;
  cached_at: string | null;
  updated_at?: string | null;
  ttl_seconds?: number;
};

export type BalanceCacheSummary = {
  updated_at: string | null;
  is_stale: boolean;
  stale_reason?: string | null;
  ttl_seconds: number;
};

export type RealtimeBalanceCachePayload = {
  balance_cache_meta?: Record<string, BalanceCacheEntry>;
  balance_cache?: BalanceCacheSummary;
};

const baseQuery = fetchBaseQuery({
  baseUrl: "/api/v1",
  prepareHeaders: (headers) => {
    const token =
      localStorage.getItem("authToken") || localStorage.getItem("token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

const reportedApiErrors = new Map<string, number>();

const reportApiError = (url: string, status: unknown, detail: unknown) => {
  if (typeof status === "number" && status < 500) {
    return;
  }
  const token =
    localStorage.getItem("authToken") || localStorage.getItem("token");
  if (!token) {
    return;
  }
  const message =
    typeof detail === "string"
      ? detail
      : JSON.stringify(detail || "API request failed").slice(0, 1000);
  const key = `${status}:${url}:${message}`.slice(0, 500);
  const now = Date.now();
  const lastSentAt = reportedApiErrors.get(key) || 0;
  if (now - lastSentAt < 30_000 || url.includes("/client-errors")) {
    return;
  }
  reportedApiErrors.set(key, now);

  fetch("/api/v1/client-errors", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      message: `API error ${status || "unknown"} on ${url}: ${message}`,
      error_type: "ApiRequestError",
      url: window.location.href,
      route: `${window.location.pathname}${window.location.search}`,
      user_agent: navigator.userAgent,
      severity: "frontend_runtime",
      recent_errors: [`${status || "unknown"} ${url}`],
      page_context: {
        title: document.title,
        path: `${window.location.pathname}${window.location.search}`,
      },
    }),
  }).catch(() => undefined);
};

const normalizeBidderOutcome = (value: unknown): BidderOutcome => {
  if (value === "improved" || value === "worsened") return value;
  if (value === "stable") return "unchanged";
  if (value === "confounded") return "confounded";
  return "insufficient_data";
};

const normalizeBidderDirection = (raw: any): BidderDirection => {
  const deltas = [
    Number(raw?.new_click ?? 0) - Number(raw?.current_click ?? 0),
    Number(raw?.new_limit ?? 0) - Number(raw?.current_limit ?? 0),
  ].filter((value) => value !== 0);
  if (!deltas.length) return "unchanged";
  if (deltas.some((value) => value > 0) && deltas.some((value) => value < 0))
    return "mixed";
  return deltas[0] > 0 ? "increase" : "decrease";
};

const normalizeBidderEvent = (raw: any): BidderEffectivenessEvent => {
  const outcomes = raw?.outcomes || {};
  const ruleLabel =
    raw?.matched_from == null || raw?.matched_to == null
      ? null
      : `Позиции ${raw.matched_from}–${raw.matched_to} · ${raw.is_xl ? "XL" : "без XL"}`;
  return {
    id: raw?.id,
    run_id: raw?.run_id ?? null,
    account_name: String(raw?.account_name || ""),
    avito_id: String(raw?.avito_id || ""),
    title: String(raw?.item_title || ""),
    model_name: raw?.model_name ?? null,
    city: raw?.city ?? null,
    changed_at: String(raw?.decided_at || ""),
    is_xl: Boolean(raw?.is_xl),
    direction:
      raw?.direction === "increase"
        ? "increase"
        : raw?.direction === "decrease"
          ? "decrease"
          : raw?.direction === "none"
            ? "unchanged"
            : normalizeBidderDirection(raw),
    outcome: normalizeBidderOutcome(outcomes.outcome),
    confidence: (["high", "medium", "low"].includes(outcomes.confidence)
      ? outcomes.confidence
      : "low") as BidderConfidence,
    confounded: Boolean(outcomes.confounded),
    confounders: outcomes.confounded
      ? [
          `вмешательство ${outcomes.interference_at ? `в ${new Date(outcomes.interference_at).toLocaleString("ru-RU")}` : "до окончания окна оценки"}`,
        ]
      : [],
    rule_id: raw?.rule_id ? String(raw.rule_id) : null,
    rule_label: ruleLabel,
    position_before: outcomes.before ?? null,
    position_1h: outcomes.h1 ?? null,
    position_3h: outcomes.h3 ?? null,
    position_6h: outcomes.h6 ?? null,
    position_day_median: outcomes.end_day ?? null,
    position_next_day_median: outcomes.next_day ?? null,
    position_delta: outcomes.delta_h6 ?? null,
    time_to_effect_hours: outcomes.time_to_effect_hours ?? null,
    cost_per_position: outcomes.cost_per_position ?? null,
    persistence: outcomes.persistence ?? null,
    click_before: raw?.current_click ?? null,
    click_after: raw?.new_click ?? null,
    limit_before: raw?.current_limit ?? null,
    limit_after: raw?.new_limit ?? null,
    explanation: raw?.error
      ? `Ошибка применения: ${raw.error}`
      : raw?.source_quality === "reconstructed"
        ? "Событие восстановлено из старого журнала; точность времени ниже."
        : [raw?.model_name, raw?.city].filter(Boolean).join(" · ") || null,
  };
};

const bidderAnalyticsParams = (filters: BidderEffectivenessFilters) => ({
  account: filters.account,
  date_from: `${filters.date_from}T00:00:00`,
  date_to: `${filters.date_to}T23:59:59`,
  ...(filters.xl === undefined ? {} : { is_xl: filters.xl }),
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : String(args?.url || "");
  const isMonitoring = url.includes("monitoring/");
  if (isMonitoring) {
    const method =
      typeof args === "string" ? "GET" : String(args?.method || "GET");
    const bodyPreview = typeof args === "string" ? undefined : args?.body;
    console.log("[monitoring api] request", { method, url, body: bodyPreview });
  }

  let result = await baseQuery(args, api, extraOptions);

  if (isMonitoring && result.error) {
    const errAny: any = result.error as any;
    const errData: any = errAny?.data;
    const errDetail =
      typeof errData === "string"
        ? errData
        : errData?.detail ||
          errData?.message ||
          errAny?.error ||
          errAny?.message ||
          null;
    console.log("[monitoring api] error", {
      url,
      status: errAny?.status,
      data: errData,
      error: errAny?.error,
      detail:
        typeof errDetail === "string" ? errDetail : JSON.stringify(errDetail),
    });
  }

  if (result.error) {
    const errAny: any = result.error as any;
    const responseHeaders = (result as any)?.meta?.response?.headers;
    const wwwAuthenticate =
      typeof responseHeaders?.get === "function"
        ? responseHeaders.get("www-authenticate")
        : null;

    if (
      shouldForceLogoutOn401({
        status: errAny?.status,
        url,
        wwwAuthenticate,
        detail: errAny?.data,
      })
    ) {
      clearStoredAuthSession();
      window.location.reload();
    }

    reportApiError(
      url,
      errAny?.status,
      errAny?.data || errAny?.error || errAny?.message,
    );
  }
  return result;
};

// Logger middleware for debugging
const loggerMiddleware = (store: any) => (next: any) => (action: any) => {
  if (action.type?.startsWith("analyticsApi/")) {
    console.log("🔄 RTK Query Action:", action.type, action);
  }
  return next(action);
};
void loggerMiddleware;

export const analyticsApi = createApi({
  reducerPath: "analyticsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Accounts",
    "DashboardSummary",
    "SpendsChart",
    "CTRChart",
    "TopModels",
    "SpendsByAccounts",
    "Campaign",
    "AnalyticsTable",
    "AccountsPerformance",
    "AI",
    "Realtime",
    "Feedback",
    "Autoload",
    "BlockedItems",
    "Subscription",
    "SheetsSync",
    "HiddenAds",
    "YesterdaySpends",
    "Monitoring",
    "MonitoringWorkbench",
    "MonitoringCpx",
    "Agents",
    "ServiceLevel",
    "MonitoringRating",
    "FeedbackSettings",
    "TaskManager",
    "MonitoringRunner",
    "AccountCabinet",
  ],
  endpoints: (builder) => ({
    getAccountCabinetAccounts: builder.query<{ accounts: any[] }, void>({
      query: () => "/account-cabinet/accounts",
      // Cabinet cards come from prepared snapshots, so we intentionally do not
      // hold them for 15 minutes. A long cache could display an account the
      // user has already moved away from when accounts.json changes.
      providesTags: ["AccountCabinet"],
    }),
    getAccountCabinet: builder.query<any, string>({
      query: (accountName) =>
        `/account-cabinet/${encodeURIComponent(accountName)}`,
      providesTags: (_result, _error, accountName) => [
        { type: "AccountCabinet", id: accountName },
      ],
      // Returned snapshots are persisted on the server, but the cabinet
      // endpoint must never display a previous account.  Drop the cache as
      // soon as the consumer unsubscribes so route/arg changes cannot
      // resurrect a stale payload.
      keepUnusedDataFor: 0,
    }),
    getAccountCabinetParserRun: builder.query<
      any,
      { accountName: string; runId: string }
    >({
      query: ({ accountName, runId }) =>
        `/account-cabinet/${encodeURIComponent(accountName)}/parser-runs/${encodeURIComponent(runId)}`,
      keepUnusedDataFor: 60,
    }),
    getAccountCabinetComments: builder.query<
      {
        account: string;
        items: Array<{
          id: number;
          account_name: string;
          body: string;
          created_at?: string | null;
          created_by?: string | null;
        }>;
        next_before_id?: number | null;
      },
      { accountName: string; limit?: number; beforeId?: number }
    >({
      query: ({ accountName, limit = 20, beforeId }) => ({
        url: `/account-cabinet/${encodeURIComponent(accountName)}/comments`,
        params: { limit, ...(beforeId ? { before_id: beforeId } : {}) },
      }),
      keepUnusedDataFor: 60,
    }),
    createAccountCabinetComment: builder.mutation<
      { id: number; account_name: string; body: string; created_at?: string | null; created_by?: string | null },
      { accountName: string; body: string }
    >({
      query: ({ accountName, body }) => ({
        url: `/account-cabinet/${encodeURIComponent(accountName)}/comments`,
        method: "POST",
        body: { body },
      }),
    }),
    updateAccountCabinetChecklist: builder.mutation<
      { checklist: any },
      { accountName: string; changes: Record<string, unknown> }
    >({
      query: ({ accountName, changes }) => ({
        url: `/account-cabinet/${encodeURIComponent(accountName)}/checklist`,
        method: "PUT",
        body: changes,
      }),
      invalidatesTags: ["AccountCabinet"],
    }),
    importAccountCabinetChecklist: builder.mutation<{ imported: number }, void>({
      query: () => ({
        url: "/account-cabinet/checklist/import-google",
        method: "POST",
      }),
      invalidatesTags: ["AccountCabinet"],
    }),
    getAccountCabinetMetricHighlightSettings: builder.query<any, void>({
      query: () => "/account-cabinet/settings/metric-highlights",
      providesTags: ["AccountCabinet"],
    }),
    updateAccountCabinetMetricHighlightSettings: builder.mutation<any, { settings: Record<string, unknown> }>({
      query: (body) => ({
        url: "/account-cabinet/settings/metric-highlights",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AccountCabinet"],
    }),
    getAccountCabinetScoreSettings: builder.query<any, void>({
      query: () => "/account-cabinet/settings/score",
      providesTags: ["AccountCabinet"],
    }),
    updateAccountCabinetScoreSettings: builder.mutation<any, { settings: Record<string, unknown> }>({
      query: (body) => ({
        url: "/account-cabinet/settings/score",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AccountCabinet"],
    }),
    importAccountCabinetScoreSettings: builder.mutation<any, void>({
      query: () => ({
        url: "/account-cabinet/settings/score/import-google",
        method: "POST",
      }),
      invalidatesTags: ["AccountCabinet"],
    }),
    // Realtime Summary
    getRealtimeSummary: builder.query<
      RealtimeSummary & RealtimeBalanceCachePayload,
      void
    >({
      query: () => "/analytics/realtime/summary",
      providesTags: ["Realtime"],
    }),

    getBlockedItems: builder.query<{ [key: string]: any[] }, void>({
      query: () => "/analytics/realtime/blocked-items",
      providesTags: ["BlockedItems"],
    }),

    getYesterdaySpends: builder.query<
      { yesterday_spends: { [key: string]: number }; updated_at: string },
      void
    >({
      query: () => "/analytics/realtime/yesterday-spends",
      providesTags: ["YesterdaySpends"],
    }),

    getRealtimeBalances: builder.query<
      {
        balances: Record<string, { balance: number; advance: number }>;
        updated_at: string;
      } & RealtimeBalanceCachePayload,
      void
    >({
      query: () => "/analytics/realtime/balances",
    }),

    getRealtimeXlMetrics: builder.query<
      RealtimeXlMetricsResponse,
      number | void
    >({
      query: (daysOff = 0) =>
        `/analytics/realtime/xl-metrics?days_off=${daysOff}`,
      providesTags: ["Realtime"],
    }),

    // AI Query
    askAI: builder.mutation<AIQueryResponse, AIQueryRequest>({
      query: (body) => ({
        url: "/ai/query",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AI"],
    }),

    helpAI: builder.mutation<AIQueryResponse, AIQueryRequest>({
      query: (body) => ({
        url: "/ai/help",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AI"],
    }),

    analyzeTable: builder.mutation<AIQueryResponse, AITableAnalysisRequest>({
      query: (body) => ({
        url: "/ai/analyze_table",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AI"],
    }),

    getSummaryAssistantSettings: builder.query<SummaryAssistantSettings, void>({
      query: () => "/ai/summary-assistant/settings",
      providesTags: ["AI"],
    }),

    updateSummaryAssistantSettings: builder.mutation<
      SummaryAssistantSettings,
      Pick<SummaryAssistantSettings, "enabled" | "system_prompt">
    >({
      query: (body) => ({
        url: "/ai/summary-assistant/settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AI"],
    }),

    runSummaryAssistant: builder.mutation<
      SummaryAssistantResponse,
      SummaryAssistantRequest
    >({
      query: (body) => ({
        url: "/ai/summary-assistant",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AI"],
    }),

    // ==================== AGENTS ====================
    listAgents: builder.query<Agent[], void>({
      query: () => "/agents",
      providesTags: ["Agents"],
    }),

    createAgent: builder.mutation<
      Agent,
      { name: string; system_prompt: string; description?: string | null }
    >({
      query: (body) => ({
        url: "/agents",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Agents"],
    }),

    updateAgent: builder.mutation<
      Agent,
      {
        agent_id: string;
        name?: string;
        system_prompt?: string;
        description?: string | null;
      }
    >({
      query: ({ agent_id, ...patch }) => ({
        url: `/agents/${encodeURIComponent(agent_id)}`,
        method: "PUT",
        body: patch,
      }),
      invalidatesTags: ["Agents"],
    }),

    deleteAgent: builder.mutation<{ success: boolean }, { agent_id: string }>({
      query: ({ agent_id }) => ({
        url: `/agents/${encodeURIComponent(agent_id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Agents"],
    }),

    listAgentThreads: builder.query<AgentThreadSummary[], { agent_id: string }>(
      {
        query: ({ agent_id }) =>
          `/agents/${encodeURIComponent(agent_id)}/threads`,
        providesTags: ["Agents"],
      },
    ),

    createAgentThread: builder.mutation<
      AgentThreadSummary,
      { agent_id: string; title?: string | null }
    >({
      query: ({ agent_id, ...body }) => ({
        url: `/agents/${encodeURIComponent(agent_id)}/threads`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Agents"],
    }),

    getAgentThread: builder.query<
      AgentThreadDetail,
      { agent_id: string; thread_id: string }
    >({
      query: ({ agent_id, thread_id }) =>
        `/agents/${encodeURIComponent(agent_id)}/threads/${encodeURIComponent(thread_id)}`,
      providesTags: ["Agents"],
    }),

    deleteAgentThread: builder.mutation<
      { success: boolean },
      { agent_id: string; thread_id: string }
    >({
      query: ({ agent_id, thread_id }) => ({
        url: `/agents/${encodeURIComponent(agent_id)}/threads/${encodeURIComponent(thread_id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Agents"],
    }),

    sendAgentMessage: builder.mutation<
      AgentThreadDetail,
      { agent_id: string; thread_id: string; text: string }
    >({
      query: ({ agent_id, thread_id, text }) => ({
        url: `/agents/${encodeURIComponent(agent_id)}/threads/${encodeURIComponent(thread_id)}/message`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: ["Agents"],
    }),

    // ==================== OPENCLAW / CLAWHUB ====================
    openclawAgent: builder.mutation<
      OpenClawAgentResponse,
      OpenClawAgentRequest
    >({
      query: (body) => ({
        url: "/openclaw/openclaw",
        method: "POST",
        body,
        responseHandler: async (response) => {
          const text = await response.text();
          try {
            return JSON.parse(text);
          } catch {
            return {
              response_text: text,
              raw_stdout: text,
              stderr: "",
            } as OpenClawAgentResponse;
          }
        },
      }),
    }),

    listOpenclawThreads: builder.query<OpenClawThreadSummary[], void>({
      query: () => "/openclaw/threads",
      providesTags: ["Agents"],
    }),

    createOpenclawThread: builder.mutation<
      OpenClawThreadSummary,
      OpenClawThreadCreateRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/threads",
        method: "POST",
        body: body || {},
      }),
      invalidatesTags: ["Agents"],
    }),

    getOpenclawThread: builder.query<
      OpenClawThreadDetail,
      { thread_id: string }
    >({
      query: ({ thread_id }) =>
        `/openclaw/threads/${encodeURIComponent(thread_id)}`,
      providesTags: ["Agents"],
    }),

    deleteOpenclawThread: builder.mutation<
      { success: boolean },
      { thread_id: string }
    >({
      query: ({ thread_id }) => ({
        url: `/openclaw/threads/${encodeURIComponent(thread_id)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Agents"],
    }),

    sendOpenclawThreadMessage: builder.mutation<
      OpenClawThreadDetail,
      { thread_id: string } & OpenClawThreadMessageRequest
    >({
      query: ({ thread_id, ...body }) => ({
        url: `/openclaw/threads/${encodeURIComponent(thread_id)}/message`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Agents"],
    }),

    startOpenclawThreadMessageJob: builder.mutation<
      OpenCodeMessageJobStartResponse,
      { thread_id: string } & OpenClawThreadMessageRequest
    >({
      query: ({ thread_id, ...body }) => ({
        url: `/openclaw/threads/${encodeURIComponent(thread_id)}/message/start`,
        method: "POST",
        body,
      }),
    }),

    getOpenclawThreadMessageJobStatus: builder.query<
      OpenCodeMessageJobStatusResponse,
      { thread_id: string; job_id: string }
    >({
      query: ({ thread_id, job_id }) =>
        `/openclaw/threads/${encodeURIComponent(thread_id)}/message/jobs/${encodeURIComponent(job_id)}`,
    }),

    getOpenclawThreadActiveRun: builder.query<
      OpenCodeRunSummary | null,
      { thread_id: string }
    >({
      query: ({ thread_id }) =>
        `/openclaw/threads/${encodeURIComponent(thread_id)}/message/active-run`,
    }),

    cancelOpenclawThreadMessageJob: builder.mutation<
      OpenCodeCancelRunResponse,
      { thread_id: string; job_id: string }
    >({
      query: ({ thread_id, job_id }) => ({
        url: `/openclaw/threads/${encodeURIComponent(thread_id)}/message/jobs/${encodeURIComponent(job_id)}/cancel`,
        method: "POST",
      }),
    }),

    listOpenclawRuntimeRuns: builder.query<OpenClawRuntimeRunsResponse, void>({
      query: () => "/openclaw/runtime/runs",
    }),

    cancelAllOpenclawRuntimeRuns: builder.mutation<
      OpenClawRuntimeCleanupResponse,
      void
    >({
      query: () => ({
        url: "/openclaw/runtime/cancel-all",
        method: "POST",
      }),
    }),

    cleanupStaleOpenclawRuntimeRuns: builder.mutation<
      OpenClawRuntimeCleanupResponse,
      void
    >({
      query: () => ({
        url: "/openclaw/runtime/cleanup-stale",
        method: "POST",
      }),
    }),

    openclawPing: builder.query<OpenCodePingResponse, void>({
      query: () => "/openclaw/ping",
      providesTags: ["Agents"],
    }),

    getOpenCodeModelPresets: builder.query<OpenCodeModelPresetsResponse, void>({
      query: () => "/openclaw/model-presets",
      providesTags: ["Agents"],
    }),

    updateOpenCodeModelPresets: builder.mutation<
      OpenCodeModelPresetsResponse,
      OpenCodeModelPresetsUpdateRequest
    >({
      query: (body) => ({
        url: "/openclaw/model-presets",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Agents"],
    }),

    getOpenclawMemory: builder.query<OpenClawMemoryResponse, void>({
      query: () => "/openclaw/memory",
      providesTags: ["Agents"],
    }),

    updateOpenclawMemory: builder.mutation<
      OpenClawMemoryResponse,
      OpenClawMemoryUpdateRequest
    >({
      query: (body) => ({
        url: "/openclaw/memory",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Agents"],
    }),

    openclawSkillsList: builder.mutation<
      OpenClawSkillsListResponse,
      OpenClawSkillsListRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/skills/list",
        method: "POST",
        body: body || {},
      }),
    }),

    openclawSkillToggle: builder.mutation<
      OpenClawSkillToggleResponse,
      OpenClawSkillToggleRequest
    >({
      query: (body) => ({
        url: "/openclaw/skills/toggle",
        method: "POST",
        body,
      }),
    }),

    clawhubSearch: builder.mutation<
      ClawHubSearchResponse,
      ClawHubSearchRequest
    >({
      query: (body) => ({
        url: "/openclaw/clawhub/search",
        method: "POST",
        body,
      }),
    }),

    clawhubInstall: builder.mutation<
      ClawHubInstallResponse,
      ClawHubInstallRequest
    >({
      query: (body) => ({
        url: "/openclaw/clawhub/install",
        method: "POST",
        body,
      }),
    }),

    openclawSystemStatus: builder.mutation<
      OpenClawSystemStatusResponse,
      OpenClawSystemStatusRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/system/status",
        method: "POST",
        body: body || {},
      }),
    }),

    openclawSystemLogs: builder.mutation<
      OpenClawSystemLogsResponse,
      OpenClawSystemLogsRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/system/logs",
        method: "POST",
        body: body || {},
      }),
    }),

    openclawSystemRestart: builder.mutation<
      OpenClawSystemRestartResponse,
      OpenClawSystemRestartRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/system/restart",
        method: "POST",
        body: body || {},
      }),
    }),

    openclawModelsList: builder.mutation<
      OpenClawModelsListResponse,
      OpenClawModelsListRequest | void
    >({
      query: (body) => ({
        url: "/openclaw/models/list",
        method: "POST",
        body: body || {},
      }),
    }),

    openclawModelsSet: builder.mutation<
      OpenClawModelsSetResponse,
      OpenClawModelsSetRequest
    >({
      query: (body) => ({
        url: "/openclaw/models/set",
        method: "POST",
        body,
      }),
    }),

    // Feedback
    getFeedback: builder.query<Review[], void>({
      query: () => "/feedback/",
      providesTags: ["Feedback"],
    }),

    syncFeedback: builder.mutation<
      { status: string; message: string },
      { reset?: boolean; lookback_days?: number }
    >({
      query: (params) => ({
        url: "/feedback/sync",
        method: "POST",
        params,
      }),
      invalidatesTags: ["Feedback"],
    }),

    getFeedbackSettings: builder.query<FeedbackSettings, void>({
      query: () => "/feedback/settings",
      providesTags: ["FeedbackSettings"],
    }),

    updateFeedbackSettings: builder.mutation<
      FeedbackSettings,
      FeedbackSettings
    >({
      query: (settings) => ({
        url: "/feedback/settings",
        method: "PUT",
        body: settings,
      }),
      invalidatesTags: ["FeedbackSettings"],
    }),

    disputeReview: builder.mutation<any, string>({
      query: (reviewId) => ({
        url: `/disputes/${reviewId}`,
        method: "POST",
      }),
      invalidatesTags: ["Feedback"],
    }),

    addNote: builder.mutation<any, { reviewId: string; text: string }>({
      query: ({ reviewId, text }) => ({
        url: `/notes/${reviewId}`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: ["Feedback"],
    }),

    markReviewAsReplied: builder.mutation<any, string>({
      query: (reviewId) => ({
        url: `/feedback/${reviewId}/mark-replied`,
        method: "POST",
      }),
      invalidatesTags: ["Feedback"],
    }),

    getChatByReview: builder.mutation<ChatData[], GetChatByReviewRequest>({
      query: (body) => ({
        url: "/messenger/chat-by-review",
        method: "POST",
        body,
      }),
    }),

    generateDisputeText: builder.mutation<
      AIQueryResponse,
      {
        review_text: string;
        author_name: string;
        chat_messages?: any[];
        chat_context_available: boolean;
        user_feedback?: string;
        previous_response?: string;
      }
    >({
      query: (body) => ({
        url: "/ai/dispute-review",
        method: "POST",
        body,
      }),
    }),

    saveReviewAiDisputeDraft: builder.mutation<
      AIReviewDraft,
      {
        reviewId: string;
        text: string;
        source: AIReviewDraftSource;
        user_feedback?: string;
      }
    >({
      query: ({ reviewId, ...body }) => ({
        url: `/feedback/${reviewId}/ai-dispute`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Feedback"],
    }),

    deleteReviewAiDisputeDraft: builder.mutation<AIReviewDraft, string>({
      query: (reviewId) => ({
        url: `/feedback/${reviewId}/ai-dispute`,
        method: "DELETE",
      }),
      invalidatesTags: ["Feedback"],
    }),

    generateReviewResponse: builder.mutation<
      { response_text: string },
      { reviewId: string; previous_text?: string; rejection_feedback?: string }
    >({
      query: ({ reviewId, ...body }) => ({
        url: `/feedback/${reviewId}/generate-response`,
        method: "POST",
        body,
      }),
    }),

    sendReviewForApproval: builder.mutation<
      any,
      { reviewId: string; response_text: string }
    >({
      query: ({ reviewId, response_text }) => ({
        url: `/feedback/${reviewId}/send-for-approval`,
        method: "POST",
        body: { response_text },
      }),
      invalidatesTags: ["Feedback"],
    }),

    // Accounts
    getAccounts: builder.query<Account[], void>({
      query: () => "/analytics/accounts",
      providesTags: ["Accounts"],
    }),

    getMonitoringAccountRating: builder.query<AccountRating, string>({
      query: (accountName) =>
        `/analytics/accounts/${encodeURIComponent(accountName)}/rating`,
      providesTags: (result, error, accountName) => [
        { type: "MonitoringRating", id: accountName },
      ],
      // Кэшируем на 15 минут на фронтенде тоже
      keepUnusedDataFor: 900,
    }),

    getAutoloadAccounts: builder.query<Account[], void>({
      query: () => "/analytics/accounts", // ИСПРАВЛЕНО
      providesTags: ["Accounts"],
    }),

    // Dashboard summary
    getDashboardSummary: builder.query<
      DashboardSummary,
      {
        startDate?: string;
        endDate?: string;
        days?: number;
        account_name?: string;
      }
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.startDate && params.endDate) {
          searchParams.append("start_date_str", params.startDate);
          searchParams.append("end_date_str", params.endDate);
        } else if (params.days) {
          searchParams.append("days", params.days.toString());
        }
        if (params.account_name) {
          searchParams.append("account_name", params.account_name);
        }
        return `/analytics/dashboard/summary?${searchParams.toString()}`;
      },
      providesTags: ["DashboardSummary"],
    }),

    // Spends chart data
    getSpendsChart: builder.query<
      ChartData,
      {
        startDate?: string;
        endDate?: string;
        days?: number;
        account_name?: string;
      }
    >({
      query: ({ startDate, endDate, days, account_name }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        if (account_name) params.append("account_name", account_name);
        return `/analytics/dashboard/spends-chart?${params.toString()}`;
      },
      providesTags: ["SpendsChart"],
    }),

    // CTR chart data
    getCTRChart: builder.query<
      ChartData,
      {
        startDate?: string;
        endDate?: string;
        days?: number;
        account_name?: string;
      }
    >({
      query: ({ startDate, endDate, days, account_name }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        if (account_name) params.append("account_name", account_name);
        return `/analytics/dashboard/ctr-chart?${params.toString()}`;
      },
      providesTags: ["CTRChart"],
    }),

    // Top models
    getTopModels: builder.query<
      TopModelData[],
      {
        startDate?: string;
        endDate?: string;
        days?: number;
        limit: number;
        account_name?: string;
      }
    >({
      query: ({ startDate, endDate, days, limit, account_name }) => {
        const params = new URLSearchParams({ limit: limit.toString() });
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        if (account_name) params.append("account_name", account_name);
        return `/analytics/dashboard/top-models?${params.toString()}`;
      },
      providesTags: ["TopModels"],
    }),

    // ==================== ADVERTISEMENTS TABLE ====================
    getAdvertisementsTable: builder.query<
      AdvertisementsTableResponse,
      {
        account_name: string;
        startDate: string;
        endDate: string;
        force_refresh?: boolean;
        fast_mode?: boolean;
      }
    >({
      query: ({
        account_name,
        startDate,
        endDate,
        force_refresh,
        fast_mode,
      }) => ({
        url: `/analytics/advertisements/${encodeURIComponent(account_name)}/table`,
        params: {
          date_from: startDate,
          date_to: endDate,
          ...(force_refresh && { force_refresh: true }),
          ...(fast_mode && { fast_mode: true }),
        },
      }),
      // Кэш на 1 час
      keepUnusedDataFor: 3600,
      providesTags: (result, error, { account_name }) => [
        { type: "AnalyticsTable", id: `${account_name}-ads` },
      ],
    }),

    // ==================== GOOGLE SHEETS SYNC ====================
    getSheetsFileInfo: builder.query<FileInfoResponse, void>({
      query: () => "/analytics/sync/info",
      providesTags: ["SheetsSync"],
    }),

    syncSheetsData: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: "/analytics/sync/sync",
        method: "POST",
      }),
      invalidatesTags: ["SheetsSync"],
    }),

    autoSyncSheets: builder.mutation<SyncResponse, void>({
      query: () => ({
        url: "/analytics/sync/auto-sync",
        method: "POST",
      }),
      invalidatesTags: ["SheetsSync"],
    }),

    // Spends by accounts
    getSpendsByAccounts: builder.query<
      { account: string; spends: number }[],
      { startDate?: string; endDate?: string; days?: number }
    >({
      query: ({ startDate, endDate, days }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        return `/analytics/dashboard/spends-by-accounts?${params.toString()}`;
      },
      providesTags: ["SpendsByAccounts"],
    }),

    // Campaign details
    getCampaignDetails: builder.query<
      CampaignMetrics,
      {
        account_name: string;
        startDate?: string;
        endDate?: string;
        days?: number;
      }
    >({
      query: ({ account_name, startDate, endDate, days }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        return `/analytics/campaign/${encodeURIComponent(account_name)}?${params.toString()}`;
      },
      providesTags: (result, error, { account_name }) => [
        { type: "Campaign", id: account_name },
      ],
    }),

    getAccountsPerformance: builder.query<
      AccountPerformanceData[],
      { startDate?: string; endDate?: string; days?: number }
    >({
      query: ({ startDate, endDate, days }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        return `/analytics/dashboard/accounts-performance?${params.toString()}`;
      },
      providesTags: ["AccountsPerformance"],
    }),

    // Autoload reports
    getAutoloadReports: builder.query<AutoloadReportsResponse, string | null>({
      query: (accountName) => {
        if (accountName && accountName !== "all") {
          return `/autoload/reports?account_name=${encodeURIComponent(accountName)}`;
        }
        return "/autoload/reports";
      },
      providesTags: ["Autoload"],
    }),

    getAutoloadUserDocsTree: builder.query<any, void>({
      query: () => "/autoload/user-docs/tree",
      providesTags: ["Autoload"],
    }),

    getAutoloadUserDocsFields: builder.query<any, string>({
      query: (slug) =>
        `/autoload/user-docs/node/${encodeURIComponent(slug)}/fields`,
      providesTags: ["Autoload"],
    }),

    getAutoloadUserDocsValues: builder.query<
      any,
      { values_link_json: string; limit?: number }
    >({
      query: ({ values_link_json, limit }) => {
        const qs = new URLSearchParams({
          values_link_json,
          ...(limit ? { limit: String(limit) } : {}),
        });
        return `/autoload/user-docs/values?${qs.toString()}`;
      },
      providesTags: ["Autoload"],
    }),

    getAutoloadReportById: builder.query<
      AutoloadReportDetails,
      { reportId: number; accountName: string }
    >({
      query: ({ reportId, accountName }) =>
        `/autoload/reports/${reportId}?account_name=${encodeURIComponent(accountName)}`,
      providesTags: (result, error, { reportId }) => [
        { type: "Autoload", id: reportId },
      ],
    }),

    getAutoloadReportItems: builder.query<
      { items: AutoloadReportItem[]; meta: any },
      {
        reportId: number;
        accountName: string;
        page?: number;
        per_page?: number;
        searchTerm?: string;
        filterType?: string;
        avitoStatusFilter?: string;
      }
    >({
      query: ({
        reportId,
        accountName,
        page = 0,
        per_page = 50,
        searchTerm,
        filterType,
        avitoStatusFilter,
      }) => {
        let url = `/autoload/reports/${reportId}/items?account_name=${encodeURIComponent(accountName)}&page=${page}&per_page=${per_page}`;
        if (searchTerm) {
          url += `&search_term=${encodeURIComponent(searchTerm)}`;
        }
        if (filterType && filterType !== "all") {
          url += `&filter_type=${encodeURIComponent(filterType)}`;
        }
        if (avitoStatusFilter && avitoStatusFilter !== "all") {
          url += `&avito_status_filter=${encodeURIComponent(avitoStatusFilter)}`;
        }
        return url;
      },
      // Кастомная сериализация для правильного отслеживания изменений параметров
      // Важно: нормализуем undefined в явные значения для правильного сравнения
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          reportId,
          accountName,
          page = 0,
          per_page = 50,
          searchTerm,
          filterType,
          avitoStatusFilter,
        } = queryArgs;
        // Явно указываем все параметры, заменяя undefined на 'all' или пустую строку
        const normalizedFilterType = filterType ?? "all";
        const normalizedAvitoStatusFilter = avitoStatusFilter ?? "all";
        const normalizedSearchTerm = searchTerm ?? "";
        return `${endpointName}(${reportId},${accountName},${page},${per_page},${normalizedSearchTerm},${normalizedFilterType},${normalizedAvitoStatusFilter})`;
      },
      providesTags: (
        result,
        error,
        { reportId, filterType, avitoStatusFilter, searchTerm, page },
      ) => [
        {
          type: "Autoload",
          id: `${reportId}-items-${filterType || "all"}-${avitoStatusFilter || "all"}-${searchTerm || ""}-${page || 0}`,
        },
      ],
    }),

    updateSubscriptionDate: builder.mutation<
      any,
      { accountName: string; next_payment_date: string }
    >({
      query: ({ accountName, next_payment_date }) => ({
        url: `/accounts/${accountName}/subscription`,
        method: "PUT",
        body: { next_payment_date },
      }),
      // Оптимистичное обновление - UI меняется мгновенно
      async onQueryStarted(
        { accountName, next_payment_date },
        { dispatch, queryFulfilled },
      ) {
        // Обновляем кэш СРАЗУ (оптимистично)
        const patchResult = dispatch(
          analyticsApi.util.updateQueryData(
            "getRealtimeSummary",
            undefined,
            (draft) => {
              if (draft.subscriptions && draft.subscriptions[accountName]) {
                draft.subscriptions[accountName].next_payment_date =
                  next_payment_date;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          // Если ошибка - откатываем изменения
          patchResult.undo();
        }
      },
      invalidatesTags: ["Realtime"],
    }),

    refreshAccountSubscriptionSource: builder.mutation<
      SubscriptionRefreshResponse,
      { accountName: string }
    >({
      query: ({ accountName }) => ({
        url: `/accounts/${accountName}/subscription/refresh`,
        method: "POST",
      }),
      invalidatesTags: ["Accounts", "Realtime"],
    }),

    updateTariff: builder.mutation<
      any,
      { accountName: string; tariff: string }
    >({
      query: ({ accountName, tariff }) => ({
        url: `/accounts/${accountName}/tariff`,
        method: "PUT",
        body: { tariff },
      }),
      // Оптимистичное обновление - UI меняется мгновенно
      async onQueryStarted(
        { accountName, tariff },
        { dispatch, queryFulfilled },
      ) {
        // 1. Обновляем getAccounts кэш
        const patchAccounts = dispatch(
          analyticsApi.util.updateQueryData(
            "getAccounts",
            undefined,
            (draft) => {
              const account = draft.find((acc) => acc.name === accountName);
              if (account) {
                account.tariff = tariff;
              }
            },
          ),
        );

        // 2. Обновляем getRealtimeSummary кэш (чтобы refetch не перезаписывал данные)
        const patchRealtime = dispatch(
          analyticsApi.util.updateQueryData(
            "getRealtimeSummary",
            undefined,
            (draft) => {
              if (draft.subscriptions && draft.subscriptions[accountName]) {
                draft.subscriptions[accountName].tariff = tariff;
              }
            },
          ),
        );

        try {
          // 3. Ждем ответ сервера
          const { data } = await queryFulfilled;
          // 4. После успеха — приводим оба кэша к ответу сервера (подтверждение)
          dispatch(
            analyticsApi.util.updateQueryData(
              "getAccounts",
              undefined,
              (draft) => {
                const account = draft.find((acc) => acc.name === accountName);
                if (account) {
                  account.tariff = data?.tariff ?? tariff;
                }
              },
            ),
          );
          dispatch(
            analyticsApi.util.updateQueryData(
              "getRealtimeSummary",
              undefined,
              (draft) => {
                if (draft.subscriptions && draft.subscriptions[accountName]) {
                  draft.subscriptions[accountName].tariff =
                    data?.tariff ?? tariff;
                }
              },
            ),
          );

          // 5. Небольшая задержка перед повторной инвалидацией для устойчивой синхронизации
          await new Promise((resolve) => setTimeout(resolve, 200));
          dispatch(analyticsApi.util.invalidateTags(["Accounts", "Realtime"]));
          // 6. Дополнительная страховка — повторная инвалидация через 400мс
          await new Promise((resolve) => setTimeout(resolve, 400));
          dispatch(analyticsApi.util.invalidateTags(["Accounts", "Realtime"]));
        } catch {
          // 5. Если ошибка - откатываем ОБА изменения
          patchAccounts.undo();
          patchRealtime.undo();
        }
      },
      invalidatesTags: ["Accounts", "Realtime"],
    }),

    updateAnalytics: builder.mutation<
      {
        success: boolean;
        message: string;
        status?: string;
        statistics?: {
          total_accounts: number;
          success_accounts: number;
          skipped_accounts: number;
          error_accounts: number;
          total_items: number;
          collection_time_sec: number;
          write_time_sec: number;
          total_time_sec: number;
        };
        success_accounts?: Array<{
          name: string;
          emoji: string;
          items_count: number;
        }>;
        skipped_accounts?: Array<{
          name: string;
          emoji: string;
          error: string;
        }>;
        error_accounts?: Array<{ name: string; emoji: string; error: string }>;
      },
      void
    >({
      query: () => ({
        url: "/autoload/analytics/update",
        method: "POST",
      }),
      invalidatesTags: ["HiddenAds", "Autoload", "Accounts", "Realtime"],
    }),

    getAnalyticsUpdateStatus: builder.query<
      {
        status: string;
        stage?: string;
        message?: string;
        started_at?: string;
        completed_at?: string;
        updated_at?: string;
        statistics?: any;
        error?: string;
      },
      void
    >({
      query: () => "/autoload/analytics/update/status",
      providesTags: ["Autoload"],
    }),

    getHiddenAdsComparison: builder.query<
      {
        status: string;
        data: {
          [accountName: string]: {
            total_active_ads: number;
            ads_in_last_report: number;
            hidden_ads_count: number;
            hidden_percentage: number;
            last_report_date: string | null;
            last_report_id: number | null;
            has_iphone?: boolean;
            error?: string;
            error_code?: string;
            status?: string;
            cache_last_updated?: string | null;
            comparison_completed_at?: string | null;
          };
        };
        message: string;
        source?: string;
        meta?: {
          total_accounts: number;
          accounts_with_hidden: number;
          accounts_with_errors: number;
          completed_at: string | null;
          cache_source: string | null;
          cache_ttl: number;
        };
      },
      void
    >({
      query: () => ({
        url: "/analytics/hidden-ads/hidden-ads-comparison",
      }),
      providesTags: ["HiddenAds"],
    }),

    refreshHiddenAdsComparison: builder.mutation<
      {
        status: string;
        started: boolean;
        message: string;
      },
      void
    >({
      query: () => ({
        url: "/analytics/hidden-ads/hidden-ads-comparison/refresh",
        method: "POST",
      }),
      invalidatesTags: ["HiddenAds"],
    }),

    getHiddenAdsComparisonProgress: builder.query<
      {
        status: string;
        message?: string;
        total_groups: number;
        done_groups: number;
        total_accounts: number;
        done_accounts: number;
        partial: Record<
          string,
          {
            total_active_ads?: number;
            ads_in_last_report?: number;
            hidden_ads_count?: number;
            hidden_percentage?: number;
            last_report_date?: string | null;
            last_report_id?: number | null;
            has_iphone?: boolean;
            error?: string;
            error_code?: string;
            status?: string;
            cache_last_updated?: string | null;
            comparison_completed_at?: string | null;
          }
        >;
        started_at?: string | null;
        updated_at?: string | null;
        finished_at?: string | null;
        meta?: {
          total_accounts: number;
          accounts_with_hidden: number;
          accounts_with_errors: number;
          completed_at: string | null;
        };
      },
      void
    >({
      query: () => "/analytics/hidden-ads/hidden-ads-comparison/progress",
      providesTags: ["HiddenAds"],
    }),

    getHiddenAdsActiveAccounts: builder.query<
      {
        accounts: Array<{
          name: string;
          display_name: string;
          emoji?: string | null;
          is_paused?: boolean;
        }>;
        count: number;
        paused_count: number;
      },
      void
    >({
      query: () => "/analytics/hidden-ads/active-accounts",
      providesTags: ["HiddenAds"],
    }),

    getHiddenAdsDetails: builder.query<
      {
        status: string;
        data: Array<{
          id: string;
          title: string;
          avito_status: string;
          price?: number;
          category?: string;
          location?: string;
          url?: string;
          note?: string;
        }>;
        message: string;
        meta: {
          account_name: string;
          active_ads_count: number;
          report_ads_count: number;
          hidden_ads_count: number;
          last_report_id?: number;
          last_report_date?: string;
          cache_updated?: string;
          data_source: string;
        };
      },
      string
    >({
      query: (accountName) =>
        `/analytics/hidden-ads/details/${encodeURIComponent(accountName)}`,
      providesTags: (result, error, accountName) => [
        { type: "HiddenAds", id: accountName },
      ],
    }),

    // Новые endpoints для миграции аналитики
    collectAllAccounts: builder.mutation<
      {
        success: boolean;
        date: string;
        overwrite: boolean;
        total_accounts: number;
        successful: number;
        failed: number;
        no_data: number;
        failed_accounts: string[];
        total_ads_processed: number;
        processing_time_seconds: number;
        results: Array<{
          account_name: string;
          status: "success" | "error" | "no_data";
          ads_count: number;
          deleted_count?: number;
          processing_time: number;
          error: string | null;
        }>;
      },
      {
        target_date?: string;
        overwrite?: boolean;
      }
    >({
      query: (params) => ({
        url: "/analytics/advertisements/collect-all-accounts",
        method: "POST",
        params,
      }),
      invalidatesTags: ["AnalyticsTable"],
    }),

    getDataStatus: builder.query<
      {
        last_auto_load: string | null;
        available_date_range: {
          from: string | null;
          to: string | null;
        };
        total_days: number;
        accounts_with_data: number;
        total_records: number;
      },
      void
    >({
      query: () => "/analytics/advertisements/data-status",
    }),

    // Analytics summaries (XLSX)
    createAnalyticsSummary: builder.mutation<
      any,
      {
        mode: "single" | "all";
        account_name?: string;
        startDate?: string;
        endDate?: string;
        days?: number;
      }
    >({
      query: ({ mode, account_name, startDate, endDate, days }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }

        if (mode === "single") {
          if (!account_name) {
            throw new Error("account_name is required for single summary");
          }
          const url = `/analytics/summary/${encodeURIComponent(account_name)}${params.toString() ? `?${params.toString()}` : ""}`;
          return {
            url,
            method: "POST",
          };
        }

        const url = `/analytics/summary/all${params.toString() ? `?${params.toString()}` : ""}`;
        return {
          url,
          method: "POST",
        };
      },
    }),

    getAnalyticsSummaries: builder.query<
      {
        success: boolean;
        summaries: {
          account_name: string;
          date_from: string;
          date_to: string;
          filename: string;
          download_url: string;
        }[];
      },
      { account_name?: string } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        const account_name =
          args && "account_name" in args ? args.account_name : undefined;
        if (account_name) {
          params.append("account_name", account_name);
        }
        const suffix = params.toString() ? `?${params.toString()}` : "";
        return `/analytics/summary/available${suffix}`;
      },
    }),

    deleteAnalyticsSummary: builder.mutation<
      { success: boolean; message: string },
      { filename: string }
    >({
      query: ({ filename }) => ({
        url: `/analytics/summary/file/${encodeURIComponent(filename)}`,
        method: "DELETE",
      }),
    }),

    deleteAllAnalyticsSummaries: builder.mutation<
      { success: boolean; deleted: number },
      void
    >({
      query: () => ({
        url: "/analytics/summary/all",
        method: "DELETE",
      }),
    }),

    getAnalyticsSummaryTask: builder.query<
      {
        success: boolean;
        task: SummaryTaskInfo;
      },
      { task_id: string }
    >({
      query: ({ task_id }) =>
        `/analytics/summary/task/${encodeURIComponent(task_id)}`,
    }),

    cancelAnalyticsSummaryTask: builder.mutation<
      { success: boolean; message: string; task: SummaryTaskInfo },
      { task_id: string }
    >({
      query: ({ task_id }) => ({
        url: `/analytics/summary/task/${encodeURIComponent(task_id)}/cancel`,
        method: "POST",
      }),
    }),

    dismissAnalyticsSummaryTask: builder.mutation<
      { success: boolean; task_id: string },
      { task_id: string }
    >({
      query: ({ task_id }) => ({
        url: `/analytics/summary/task/${encodeURIComponent(task_id)}`,
        method: "DELETE",
      }),
    }),

    createSummaryTable: builder.mutation<
      { success: boolean; task_id: string },
      {
        account_name: string;
        startDate?: string;
        endDate?: string;
        days?: number;
      }
    >({
      query: ({ account_name, startDate, endDate, days }) => {
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append("start_date_str", startDate);
          params.append("end_date_str", endDate);
        } else if (days) {
          params.append("days", days.toString());
        }
        return {
          url: `/analytics/summary/table/${encodeURIComponent(account_name)}?${params.toString()}`,
          method: "POST",
        };
      },
    }),

    createSummaryTableBatch: builder.mutation<
      { success: boolean; status: string; tasks: SummaryBatchStartItem[] },
      {
        account_names: string[];
        startDate?: string;
        endDate?: string;
        days?: number;
      }
    >({
      query: ({ account_names, startDate, endDate, days }) => ({
        url: "/analytics/summary/table-batch",
        method: "POST",
        body: {
          account_names,
          ...(startDate ? { start_date_str: startDate } : {}),
          ...(endDate ? { end_date_str: endDate } : {}),
          ...(!startDate && !endDate && days ? { days } : {}),
        },
      }),
    }),

    getSummaryTableData: builder.query<
      { success: boolean; data: SummaryTableData },
      { task_id: string }
    >({
      query: ({ task_id }) =>
        `/analytics/summary/table/${encodeURIComponent(task_id)}/data`,
    }),

    updateSummaryManualFields: builder.mutation<
      { success: boolean; row_key: string; manual_fields: SummaryManualFields },
      {
        row_key: string;
        account_name: string;
        title: string;
        category: string;
        summary_id?: string | null;
        avito_sales_count?: number | null;
        total_sales_count?: number | null;
        margin_note?: string | null;
        comment?: string | null;
        client_comment?: string | null;
      }
    >({
      query: ({ row_key, ...body }) => ({
        url: `/analytics/summary/manual-fields/${encodeURIComponent(row_key)}`,
        method: "PATCH",
        body,
      }),
    }),

    updateSummaryManualCellHighlight: builder.mutation<
      {
        success: boolean;
        row_key: string;
        manual_cell_highlights: Partial<
          Record<SummaryManualHighlightColumnKey, SummaryManualHighlightColor>
        >;
      },
      {
        row_key: string;
        account_name: string;
        title: string;
        category: string;
        summary_id: string;
        column_key: SummaryManualHighlightColumnKey;
        color: SummaryManualHighlightColor | null;
      }
    >({
      query: ({ row_key, ...body }) => ({
        url: `/analytics/summary/manual-highlights/${encodeURIComponent(row_key)}`,
        method: "PATCH",
        body,
      }),
    }),

    clearSummaryManualCellHighlights: builder.mutation<
      { success: boolean; summary_id: string },
      { summary_id: string }
    >({
      query: ({ summary_id }) => ({
        url: `/analytics/summary/manual-highlights/${encodeURIComponent(summary_id)}`,
        method: "DELETE",
      }),
    }),

    getSavedSummaries: builder.query<
      { success: boolean; items: SavedSummaryListItem[] },
      void
    >({
      query: () => "/analytics/summary/saved",
    }),

    getSavedSummary: builder.query<
      { success: boolean; data: SummaryTableData },
      { summary_id: string }
    >({
      query: ({ summary_id }) =>
        `/analytics/summary/saved/${encodeURIComponent(summary_id)}`,
    }),

    deleteSavedSummary: builder.mutation<
      { success: boolean; summary_id: string },
      { summary_id: string }
    >({
      query: ({ summary_id }) => ({
        url: `/analytics/summary/saved/${encodeURIComponent(summary_id)}`,
        method: "DELETE",
      }),
    }),

    // ── Share management (с авторизацией) ──────────────────────────────────

    createSummaryShare: builder.mutation<
      CreateSummaryShareResponse,
      {
        summary_id: string;
        account_name?: string;
        client_name?: string;
        expires_in_days: number;
        notify_bot?: boolean;
      }
    >({
      async queryFn(body, api, extraOptions, baseQuery) {
        const primaryResult = await baseQuery({
          url: "/analytics/summary/shares/create",
          method: "POST",
          body,
        });

        const primaryData = (primaryResult as { data?: any }).data;
        if (
          primaryData?.share?.token ||
          (primaryData?.mode === "group_bulk" &&
            Array.isArray(primaryData?.shares))
        ) {
          return { data: primaryData };
        }

        const primaryError = (primaryResult as { error?: FetchBaseQueryError })
          .error;
        const shouldTryLegacy =
          primaryError?.status === 404 ||
          primaryError?.status === 405 ||
          !primaryData;

        if (!shouldTryLegacy) {
          return primaryResult as { data: CreateSummaryShareResponse };
        }

        const legacyResult = await baseQuery({
          url: "/analytics/summary/shares",
          method: "POST",
          body,
        });

        const legacyData = (legacyResult as { data?: any }).data;
        if (legacyData?.share?.token) {
          return { data: { ...legacyData, mode: "single" } };
        }

        if (legacyData?.status === "started" && legacyData?.task_id) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: "Incompatible backend version",
              data: {
                detail:
                  "На сервере задеплоена старая версия API сводок. Нужен backend с поддержкой POST /api/v1/analytics/summary/shares/create.",
              },
            } as FetchBaseQueryError,
          };
        }

        const legacyError = (legacyResult as { error?: FetchBaseQueryError })
          .error;
        if (legacyError) {
          return { error: legacyError };
        }

        return {
          error: {
            status: "CUSTOM_ERROR",
            error: "Unexpected response",
            data: {
              detail:
                "Сервер вернул неожиданный ответ при создании ссылки на сводку.",
            },
          } as FetchBaseQueryError,
        };
      },
    }),

    getSummaryShares: builder.query<
      { success: boolean; shares: SummaryShareItem[] },
      void
    >({
      query: () => "/analytics/summary/shares",
    }),

    deleteSummaryShare: builder.mutation<
      { success: boolean },
      { share_id: string }
    >({
      query: ({ share_id }) => ({
        url: `/analytics/summary/shares/${encodeURIComponent(share_id)}`,
        method: "DELETE",
      }),
    }),

    listAnalyticsSummaryTasks: builder.query<
      {
        success: boolean;
        tasks: SummaryTaskInfo[];
      },
      { active_only?: boolean; limit?: number } | void
    >({
      query: (params) => {
        const p = (params as any) || {};
        return {
          url: "/analytics/summary/tasks",
          params: {
            active_only: p.active_only ?? true,
            limit: p.limit ?? 30,
          },
        };
      },
    }),

    runServiceLevelCollection: builder.mutation<
      ServiceLevelRunResponse,
      ServiceLevelRunRequest
    >({
      query: (body) => ({
        url: "/analytics/service-level/run",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    cancelServiceLevelTask: builder.mutation<
      { success: boolean; message: string },
      { task_id: string }
    >({
      query: ({ task_id }) => ({
        url: `/analytics/service-level/cancel?task_id=${encodeURIComponent(task_id)}`,
        method: "POST",
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    getServiceLevelAccounts: builder.query<
      {
        success: boolean;
        accounts: ServiceLevelSourceAccount[];
        total: number;
      },
      { accounts_json?: string } | void
    >({
      query: (params) => {
        const p = (params as any) || {};
        return {
          url: "/analytics/service-level/accounts",
          params: p.accounts_json
            ? { accounts_json: p.accounts_json }
            : undefined,
        };
      },
      providesTags: ["ServiceLevel"],
    }),

    getServiceLevelAccountSettings: builder.query<
      {
        success: boolean;
        settings: ServiceLevelAccountLoginSetting[];
        total: number;
      },
      void
    >({
      query: () => ({
        url: "/analytics/service-level/account-settings",
      }),
      providesTags: ["ServiceLevel"],
    }),

    getServiceLevelProfileGroupBindings: builder.query<
      ServiceLevelProfileGroupBindingsResponse,
      void
    >({
      query: () => ({
        url: "/analytics/service-level/profile-group-bindings",
      }),
      providesTags: ["ServiceLevel"],
    }),

    updateServiceLevelProfileGroupBindings: builder.mutation<
      ServiceLevelProfileGroupBindingsResponse,
      ServiceLevelProfileGroupBindingsUpdateRequest
    >({
      query: (body) => ({
        url: "/analytics/service-level/profile-group-bindings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    upsertServiceLevelAccountSettings: builder.mutation<
      {
        success: boolean;
        settings: ServiceLevelAccountLoginSetting[];
        total: number;
      },
      ServiceLevelAccountLoginSettingsUpsertRequest
    >({
      query: (body) => ({
        url: "/analytics/service-level/account-settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    getLastServiceLevelTask: builder.query<
      { success: boolean; task: ServiceLevelTask },
      {
        completed_only?: boolean;
        collection_mode?:
          | "service_level"
          | "agency_primary"
          | "min_balance"
          | "advance_withdrawal"
          | "wallet_to_advance";
      } | void
    >({
      query: (params) => {
        const p = (params as any) || {};
        return {
          url: "/analytics/service-level/last",
          params: {
            completed_only: p.completed_only ?? true,
            collection_mode: p.collection_mode ?? "service_level",
          },
        };
      },
      providesTags: ["ServiceLevel"],
    }),

    uploadServiceLevelToSheets: builder.mutation<
      ServiceLevelUploadResponse,
      ServiceLevelUploadRequest | void
    >({
      query: (body) => ({
        url: "/analytics/service-level/upload",
        method: "POST",
        body: body ?? {},
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    getServiceLevelTask: builder.query<
      { success: boolean; task: ServiceLevelTask },
      { task_id: string }
    >({
      query: ({ task_id }) =>
        `/analytics/service-level/task/${encodeURIComponent(task_id)}`,
      providesTags: ["ServiceLevel"],
    }),

    listServiceLevelTasks: builder.query<
      { success: boolean; tasks: ServiceLevelTask[] },
      {
        active_only?: boolean;
        limit?: number;
        collection_mode?:
          | "service_level"
          | "agency_primary"
          | "min_balance"
          | "advance_withdrawal"
          | "wallet_to_advance";
      } | void
    >({
      query: (params) => {
        const p = (params as any) || {};
        return {
          url: "/analytics/service-level/tasks",
          params: {
            active_only: p.active_only ?? true,
            limit: p.limit ?? 30,
            collection_mode: p.collection_mode ?? "service_level",
          },
        };
      },
      providesTags: ["ServiceLevel"],
    }),

    getServiceLevelSchedule: builder.query<ScheduleSettingsResponse, void>({
      query: () => "/analytics/service-level/schedule",
      providesTags: ["ServiceLevel"],
    }),

    updateServiceLevelSchedule: builder.mutation<
      ScheduleSettingsResponse,
      ScheduleSettings
    >({
      query: (body) => ({
        url: "/analytics/service-level/schedule",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    getServiceLevelHistory: builder.query<
      ServiceLevelHistoryResponse,
      { days?: number } | void
    >({
      query: (p) => ({
        url: "/analytics/service-level/history",
        params: { days: (p && p.days) ?? 7 },
      }),
      providesTags: ["ServiceLevel"],
    }),

    writeServiceLevelHistoryToReport: builder.mutation<
      ServiceLevelHistoryWriteResponse,
      ServiceLevelHistoryWriteRequest
    >({
      query: (body) => ({
        url: "/analytics/service-level/history/write-to-report",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ServiceLevel"],
    }),

    // ==================== MONITORING (POSITIONS) ====================
    getMonitoringConfig: builder.query<any, void>({
      query: () => "/monitoring/config",
      providesTags: ["Monitoring"],
    }),

    setMonitoringConfig: builder.mutation<
      any,
      {
        csv_url?: string;
        promotion_list_url?: string;
        avtools_api_url?: string;
        avtools_schedule_enabled?: boolean;
        avtools_schedule_times?: string[];
      }
    >({
      query: (body) => ({
        url: "/monitoring/config",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    fetchAvtoolsCsv: builder.mutation<
      { status: string; report_url: string; message: string },
      void
    >({
      query: () => ({
        url: "/monitoring/fetch-avtools-csv",
        method: "POST",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    refreshMonitoringPositionsCache: builder.mutation<
      {
        status: string;
        message: string;
        started_warmup: boolean;
        warmup_in_progress: boolean;
        items_count: number;
        positions_version: string;
        accounts_total: number;
        accounts_checked: number;
        accounts_with_positions: number;
        accounts_without_positions: number;
        accounts_check_errors: number;
        positions_health_summary?: {
          checked_days: { today: string; yesterday: string };
          problem_cities_count: number;
          problem_cities: Array<{
            city: string;
            ads_total: number;
            ads_without_positions_yesterday: number;
            ads_without_positions_today: number;
            has_any_positions_yesterday: boolean;
            has_any_positions_today: boolean;
            sample_item_ids: string[];
            sample_titles: string[];
          }>;
          healthy_cities_count: number;
          checked_ads_count: number;
          updated_at: string | null;
        };
      },
      { warmup?: boolean } | void
    >({
      query: (body) => ({
        url: "/monitoring/positions-cache/refresh",
        method: "POST",
        body: body ?? { warmup: true },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringDisableStatusConfig: builder.query<
      {
        enabled: boolean;
        check_interval_minutes: number;
        confirm_threshold_per_account: number;
        excluded_accounts: string[];
        status_trigger: string;
        pending_requests: Array<{
          request_id: string;
          account: string;
          sheet_name: string;
          detected_count: number;
          detected_ad_ids: string[];
          created_at: string;
          updated_at?: string | null;
        }>;
        last_run_at?: string | null;
        last_run_summary?: Record<string, any> | null;
      },
      void
    >({
      query: () => "/monitoring/disable-promoted/config",
      providesTags: ["Monitoring"],
    }),

    setMonitoringDisableStatusConfig: builder.mutation<
      {
        enabled: boolean;
        check_interval_minutes: number;
        confirm_threshold_per_account: number;
        excluded_accounts: string[];
        status_trigger: string;
        pending_requests: Array<{
          request_id: string;
          account: string;
          sheet_name: string;
          detected_count: number;
          detected_ad_ids: string[];
          created_at: string;
          updated_at?: string | null;
        }>;
        last_run_at?: string | null;
        last_run_summary?: Record<string, any> | null;
      },
      {
        enabled?: boolean;
        check_interval_minutes?: number;
        confirm_threshold_per_account?: number;
        excluded_accounts?: string[];
        status_trigger?: string;
      }
    >({
      query: (body) => ({
        url: "/monitoring/disable-promoted/config",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    runMonitoringDisableStatusCheck: builder.mutation<
      {
        status: string;
        reason?: string | null;
        summary?: Record<string, any> | null;
        config: {
          enabled: boolean;
          check_interval_minutes: number;
          confirm_threshold_per_account: number;
          excluded_accounts: string[];
          status_trigger: string;
          pending_requests: Array<{
            request_id: string;
            account: string;
            sheet_name: string;
            detected_count: number;
            detected_ad_ids: string[];
            created_at: string;
            updated_at?: string | null;
          }>;
          last_run_at?: string | null;
          last_run_summary?: Record<string, any> | null;
        };
      },
      { force?: boolean } | void
    >({
      query: (body) => ({
        url: "/monitoring/disable-promoted/run",
        method: "POST",
        body: body ?? { force: true },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringAnalyticsModels: builder.query<
      AnalyticsModelsResponse,
      { account: string; date_from: string; date_to: string }
    >({
      query: (params) => ({
        url: "/monitoring/analytics/models",
        method: "GET",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringAnalyticsTimeline: builder.query<
      AnalyticsTimelineResponse,
      {
        account: string;
        date_from: string;
        date_to: string;
        avito_id?: string;
        model_name?: string;
      }
    >({
      query: (params) => ({
        url: "/monitoring/analytics/timeline",
        method: "GET",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    // ==================== TRANSFER FUNDS ====================
    transferFunds: builder.mutation<
      ServiceLevelRunResponse,
      {
        accountName: string;
        direction: "wallet_to_advance" | "advance_to_wallet";
        amount: string;
      }
    >({
      query: ({ accountName, direction, amount }) => ({
        url: "/analytics/service-level/run",
        method: "POST",
        body: {
          account_names: [accountName],
          collection_mode:
            direction === "wallet_to_advance"
              ? "wallet_to_advance"
              : "advance_withdrawal",
          amount_to_withdraw: amount,
          headless: true,
          skip_login_with_token: false,
          skip_on_login_fail: true,
          auto_login_with_password_manager: true,
          profile_retries: 3,
        },
      }),
      invalidatesTags: ["ServiceLevel", "Realtime"],
    }),

    applyMonitoringDisableStatusPendingAction: builder.mutation<
      {
        request_id: string;
        action: "disable" | "dismiss";
        status: string;
        message: string;
        applied?: {
          sheet_name: string;
          promoted_value: boolean;
          summary: {
            total: number;
            deduplicated: number;
            updated: number;
            noop: number;
            not_found: number;
            error: number;
          };
          errors?: string[];
        } | null;
        config: {
          enabled: boolean;
          check_interval_minutes: number;
          confirm_threshold_per_account: number;
          excluded_accounts: string[];
          status_trigger: string;
          pending_requests: Array<{
            request_id: string;
            account: string;
            sheet_name: string;
            detected_count: number;
            detected_ad_ids: string[];
            created_at: string;
            updated_at?: string | null;
          }>;
          last_run_at?: string | null;
          last_run_summary?: Record<string, any> | null;
        };
      },
      { request_id: string; action: "disable" | "dismiss" }
    >({
      query: (body) => ({
        url: "/monitoring/disable-promoted/pending/apply",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    runMonitoringPlanningScan: builder.mutation<
      {
        status: string;
        status_trigger: string;
        date_range: { from: string; to: string };
        accounts_total: number;
        accounts_with_matches: number;
        matches_total: number;
        editable_total: number;
        readonly_total: number;
        groups: Array<{
          account: string;
          display_name: string;
          sheet_name: string;
          total_ads: number;
          editable_ads: number;
          readonly_ads: number;
          ads: Array<{
            avito_id: string;
            autoload_id?: string | null;
            row_index: number;
            model: string;
            quality: string;
            model_key: string;
            status: string;
            status_display: string;
            click: number | null;
            limit: number | null;
            promoted: boolean;
            xl: boolean;
            editable: boolean;
            readonly_reason?: string | null;
            impressions: number;
            views: number;
            contacts: number;
            spends: number;
            price: number | null;
          }>;
        }>;
        errors: string[];
      },
      void
    >({
      query: () => ({
        url: "/monitoring/planning/run",
        method: "POST",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringPlanningRecommendations: builder.mutation<
      {
        promoted_ads_per_model: number;
        recommendations: Array<{
          account: string;
          avito_id: string;
          promoted: boolean;
          xl: boolean;
          click: number | null;
          limit: number | null;
          sources_count: number;
          status: "recommended" | "skipped";
          reason?: string | null;
        }>;
        errors: string[];
      },
      void
    >({
      query: () => ({
        url: "/monitoring/planning/recommendations",
        method: "POST",
      }),
    }),

    applyMonitoringPlanningUpdates: builder.mutation<
      {
        summary: {
          total: number;
          deduplicated: number;
          updated: number;
          noop: number;
          not_found: number;
          readonly: number;
          error: number;
        };
        results: Array<{
          account: string;
          avito_id: string;
          status: "updated" | "noop" | "not_found" | "readonly" | "error";
          click?: number | null;
          limit?: number | null;
          promoted?: boolean | null;
          xl?: boolean | null;
          error?: string | null;
        }>;
      },
      {
        updates: Array<{
          account: string;
          avito_id: string;
          click?: number | null;
          limit?: number | null;
          promoted?: boolean | null;
          xl?: boolean | null;
        }>;
      }
    >({
      query: (body) => ({
        url: "/monitoring/planning/apply",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringPlanningPrices: builder.mutation<
      MonitoringPlanningPricesOut,
      MonitoringPlanningPricesIn
    >({
      query: (body) => ({
        url: "/monitoring/planning/prices",
        method: "POST",
        body,
      }),
    }),

    getMonitoringAccounts: builder.query<
      {
        accounts: Array<{
          name: string;
          display_name: string;
          found_in_promotion_list: boolean;
          promotion_list_key: string | null;
          items_count: number;
          tz_offset_from_msk?: string;
          work_mode?: string;
          night_mode_present?: boolean;
          night_mode_enabled?: boolean | null;
          night_mode_action?: string | null;
          disable_time?: string | null;
          enable_time?: string | null;
          timezone?: string | null;
          is_mass_posting?: boolean;
          data_source?: string;
        }>;
        count: number;
      },
      void
    >({
      query: () => "/monitoring/accounts",
      providesTags: ["Monitoring"],
    }),

    getMonitoringView: builder.query<
      any,
      {
        account?: string;
        q?: string;
        only_xl?: boolean;
        only_promoted?: boolean;
        limit?: number;
        offset?: number;
        positions_source?: string;
      }
    >({
      query: (params) => ({
        url: "/monitoring/view",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringWorkspaceSnapshot: builder.query<any, { account: string }>({
      query: ({ account }) => ({
        url: "/monitoring/workspace-snapshot",
        params: { account },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringSpendGuard: builder.query<any, { account: string }>({
      query: ({ account }) => ({
        url: `/monitoring/spend/${encodeURIComponent(account)}`,
      }),
      providesTags: ["Monitoring"],
    }),

    putMonitoringSpendPolicy: builder.mutation<
      any,
      {
        account: string;
        catalog?: string;
        scope_type: "account" | "model" | "ad";
        subject_key?: string;
        subject_label?: string;
        daily_limit_rub: number;
        enabled?: boolean;
      }
    >({
      query: (body) => ({
        url: "/monitoring/spend/policy",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    deleteMonitoringSpendPolicy: builder.mutation<any, number>({
      query: (policyId) => ({
        url: `/monitoring/spend/policy/${policyId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringWorkspaceManifest: builder.query<
      any,
      { accounts?: string[] } | void
    >({
      query: (params) => ({
        url: "/monitoring/workspace-manifest",
        params: params?.accounts?.length
          ? { accounts: params.accounts }
          : undefined,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringCatalogView: builder.query<
      any,
      {
        catalog: "samsung" | "xiaomi" | "extra_products";
        account: string;
        q?: string;
        only_xl?: boolean;
        only_promoted?: boolean;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ catalog, ...params }) => ({
        url: `/monitoring/catalog/${encodeURIComponent(catalog)}/view`,
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringExtraProductsOverview: builder.query<any, void>({
      query: () => "/monitoring/extra-products/overview",
      providesTags: ["Monitoring"],
    }),

    getMonitoringExtraProductSources: builder.query<any, void>({
      query: () => "/monitoring/extra-products/sources",
      providesTags: ["Monitoring"],
    }),

    saveMonitoringExtraProductSource: builder.mutation<any, { account_name?: string; spreadsheet_url_or_id: string; settings_sheet?: string; enabled?: boolean }>({
      query: (body) => ({ url: "/monitoring/extra-products/sources", method: "PUT", body }),
      invalidatesTags: ["Monitoring"],
    }),

    deleteMonitoringExtraProductSource: builder.mutation<any, number>({
      query: (sourceId) => ({ url: `/monitoring/extra-products/sources/${sourceId}`, method: "DELETE" }),
      invalidatesTags: ["Monitoring"],
    }),

    runMonitoringExtraProductsSync: builder.mutation<any, { source_ids?: number[] } | void>({
      query: (body) => ({ url: "/monitoring/extra-products/sync", method: "POST", body }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringCatalogConfig: builder.query<
      {
        enabled: boolean;
        daily_time: string;
        catalogs_enabled: Record<string, boolean>;
        accounts_include: string[];
        accounts_exclude: string[];
        stale_after_days: number;
        keyword_rules: Record<string, string[]>;
        blacklist_keywords: string[];
        last_sync: string | null;
      },
      void
    >({
      query: () => "/monitoring/catalog/config",
      providesTags: ["Monitoring"],
    }),

    updateMonitoringCatalogConfig: builder.mutation<
      any,
      {
        enabled?: boolean;
        daily_time?: string;
        catalogs_enabled?: Record<string, boolean>;
        accounts_include?: string[];
        accounts_exclude?: string[];
        stale_after_days?: number;
        keyword_rules?: Record<string, string[]>;
        blacklist_keywords?: string[];
      }
    >({
      query: (body) => ({
        url: "/monitoring/catalog/config",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    runMonitoringCatalogSync: builder.mutation<
      {
        run_id: number;
        status: string;
        accounts_total: number;
        api_groups_total: number;
        api_requests_made: number;
        fetched_total: number;
        accepted_total: number;
        filtered_total: number;
        errors: string[];
        preview: boolean;
      },
      {
        preview?: boolean;
        catalogs?: Array<"samsung" | "xiaomi" | "extra_products">;
        account_names?: string[];
      }
    >({
      query: (body) => ({
        url: "/monitoring/catalog/sync/run",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringMetrics: builder.query<
      {
        metrics_by_ad_id: Record<
          string,
          {
            ad_id: string;
            impressions: number;
            views: number;
            contacts: number;
            spends: number;
            price: number | null;
            ctr: number;
            cpl: number;
            cpv: number;
            source: "db" | "api";
          }
        >;
        daily_impressions_by_ad_id: Record<string, Record<string, number>>;
        daily_views_by_ad_id: Record<string, Record<string, number>>;
        daily_contacts_by_ad_id: Record<string, Record<string, number>>;
        daily_prices_by_ad_id: Record<string, Record<string, number>>;
        date_range: { from: string; to: string };
        source: "db" | "api" | "mixed";
      },
      { account: string; date_from: string; date_to: string }
    >({
      query: ({ account, date_from, date_to }) => ({
        url: "/monitoring/metrics",
        params: { account, date_from, date_to },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPriceHistory: builder.query<
      {
        account: string;
        normalized_account: string;
        ad_id: string;
        date_range: { from: string; to: string };
        points: Array<{ date: string; price: number | null }>;
        stats: {
          current: number;
          min: number;
          max: number;
          delta_abs: number;
          delta_pct: number | null;
          changes_count: number;
        } | null;
        source: "db";
      },
      { account: string; ad_id: string; date_from: string; date_to: string }
    >({
      query: ({ account, ad_id, date_from, date_to }) => ({
        url: "/monitoring/price-history",
        params: { account, ad_id, date_from, date_to },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPriceDynamicsGroups: builder.query<
      {
        account: string;
        normalized_account: string;
        date_range: { from: string; to: string };
        filters: {
          category_q: string;
          title_q: string;
          statuses: string[];
          sort: string;
        };
        items: Array<{
          group_key: string;
          category: string;
          title: string;
          ads_count: number;
          latest_range: { min: number | null; max: number | null };
          period_range: { min: number | null; max: number | null };
          delta_range: {
            min: number | null;
            max: number | null;
            pct_min: number | null;
            pct_max: number | null;
          };
          series: Array<{
            date: string;
            min: number | null;
            max: number | null;
          }>;
        }>;
        pagination: {
          page: number;
          page_size: number;
          total: number;
          total_pages: number;
        };
        source: "db";
      },
      {
        account: string;
        date_from: string;
        date_to: string;
        category_q?: string;
        title_q?: string;
        statuses?: string[];
        page?: number;
        page_size?: number;
        sort?: string;
      }
    >({
      query: ({ statuses, ...rest }) => ({
        url: "/monitoring/price-dynamics/groups",
        params: {
          ...rest,
          statuses_csv:
            Array.isArray(statuses) && statuses.length > 0
              ? statuses.join(",")
              : undefined,
        },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPriceDynamicsGroupAds: builder.query<
      {
        account: string;
        normalized_account: string;
        group: { category: string; title: string };
        date_range: { from: string; to: string };
        filters: { q: string; statuses: string[] };
        items: Array<{
          ad_id: string;
          title: string;
          status: string;
          latest_price: number | null;
          delta_abs: number | null;
          delta_pct: number | null;
          changes_count: number;
          series: Array<{ date: string; price: number | null }>;
        }>;
        pagination: {
          page: number;
          page_size: number;
          total: number;
          total_pages: number;
        };
        source: "db";
      },
      {
        account: string;
        date_from: string;
        date_to: string;
        category: string;
        title: string;
        statuses?: string[];
        q?: string;
        page?: number;
        page_size?: number;
      }
    >({
      query: ({ statuses, ...rest }) => ({
        url: "/monitoring/price-dynamics/group-ads",
        params: {
          ...rest,
          statuses_csv:
            Array.isArray(statuses) && statuses.length > 0
              ? statuses.join(",")
              : undefined,
        },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringReportSummary: builder.query<
      {
        account: string;
        normalized_account: string;
        date_range: { from: string | null; to: string | null };
        weekly_totals: {
          spends: number | null;
          impressions: number | null;
          views: number | null;
          favorites: number | null;
          contacts: number | null;
          chats: number | null;
          phone_views: number | null;
          phone_and_chat: number | null;
          calls_total: number | null;
          calls_primary: number | null;
          calls_missed: number | null;
        };
        yesterday: {
          spends: number | null;
          impressions: number | null;
          views: number | null;
          favorites: number | null;
          contacts: number | null;
          chats: number | null;
          phone_views: number | null;
          phone_and_chat: number | null;
          calls_total: number | null;
          calls_primary: number | null;
          calls_missed: number | null;
        } | null;
        raw_weekly_totals?: Record<string, any>;
        raw_yesterday?: Record<string, any> | null;
        daily: Array<{
          date: string;
          label: string;
          has_data: boolean;
          metrics: {
            spends: number | null;
            impressions: number | null;
            views: number | null;
            favorites: number | null;
            contacts: number | null;
            chats: number | null;
            phone_views: number | null;
            phone_and_chat: number | null;
            calls_total: number | null;
            calls_primary: number | null;
            calls_missed: number | null;
          } | null;
          raw_metrics?: Record<string, any> | null;
        }>;
      },
      { account: string; days?: number; date_from?: string; date_to?: string }
    >({
      query: ({ account, days = 7, date_from, date_to }) => ({
        url: "/monitoring/report-summary",
        params: { account, days, date_from, date_to },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringCategories: builder.query<
      {
        account: string;
        normalized_account: string;
        date_range: { from: string; to: string };
        categories: string[];
      },
      { account: string; date_from: string; date_to: string }
    >({
      query: ({ account, date_from, date_to }) => ({
        url: "/monitoring/categories",
        params: { account, date_from, date_to },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringAccountComment: builder.query<
      {
        account: string;
        report_date: string;
        comment: string;
        version: number;
        google_sync_status: "empty" | "pending" | "synced";
        google_synced_at: string | null;
        google_sync_error: string | null;
        updated_at: string | null;
        updated_by: string | null;
      },
      { account: string }
    >({
      query: ({ account }) => ({
        url: "/monitoring/account-comment",
        params: { account },
      }),
      providesTags: (_result, _error, { account }) => [
        { type: "Monitoring", id: `account-comment:${account}` },
      ],
    }),

    getMonitoringAccountComments: builder.query<
      {
        account: string;
        date_from: string;
        date_to: string;
        comments_by_date: Record<string, string>;
      },
      { account: string; date_from: string; date_to: string }
    >({
      query: ({ account, date_from, date_to }) => ({
        url: "/monitoring/account-comments",
        params: { account, date_from, date_to },
      }),
      providesTags: (_result, _error, { account }) => [
        { type: "Monitoring", id: `account-comment:${account}` },
      ],
    }),

    saveMonitoringAccountComment: builder.mutation<
      {
        account: string;
        report_date: string;
        comment: string;
        version: number;
        google_sync_status: "pending" | "synced";
        google_synced_at: string | null;
        google_sync_error: string | null;
        updated_at: string | null;
        updated_by: string | null;
      },
      { account: string; comment: string }
    >({
      query: (body) => ({
        url: "/monitoring/account-comment",
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { account }) => [
        { type: "Monitoring", id: `account-comment:${account}` },
      ],
    }),

    getMonitoringClickLimit: builder.query<
      {
        sheet_name: string;
        items: Record<
          string,
          {
            avito_id: string;
            click: number | null;
            limit: number | null;
            desired_position?: number | null;
            comment?: string | null;
            xl?: boolean | null;
            promoted?: boolean | null;
          }
        >;
      },
      { account: string; force?: boolean }
    >({
      query: ({ account, force = false }) => ({
        url: "/monitoring/click-limit",
        params: { account, force },
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const account = String(queryArgs?.account || "").trim();
        return `${endpointName}(${account})`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const prevAccount = String(previousArg?.account || "").trim();
        const nextAccount = String(currentArg?.account || "").trim();
        const prevForce = Boolean(previousArg?.force);
        const nextForce = Boolean(currentArg?.force);
        return prevAccount !== nextAccount || prevForce !== nextForce;
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringPromotionSyncRequest: builder.query<
      {
        id: string;
        account: string;
        status: "queued" | "running" | "succeeded" | "failed";
        requested_at: string | null;
        started_at: string | null;
        finished_at: string | null;
        attempts: number;
        result: { changed?: number; created: number; updated: number; removed: number; unchanged: number; errors: string[] } | null;
        error: string | null;
        coalesced?: boolean;
      } | null,
      { account: string }
    >({
      query: ({ account }) => ({
        url: "/monitoring/promotion-sync/request",
        params: { account },
      }),
      providesTags: (_result, _error, { account }) => [{ type: "Monitoring", id: `promotion-sync:${account}` }],
    }),

    requestMonitoringPromotionSync: builder.mutation<
      {
        id: string;
        account: string;
        status: "queued" | "running" | "succeeded" | "failed";
        requested_at: string | null;
        started_at: string | null;
        finished_at: string | null;
        attempts: number;
        result: { changed?: number; created: number; updated: number; removed: number; unchanged: number; errors: string[] } | null;
        error: string | null;
        coalesced?: boolean;
      },
      { account: string }
    >({
      query: (body) => ({
        url: "/monitoring/promotion-sync/request",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { account }) => [{ type: "Monitoring", id: `promotion-sync:${account}` }],
    }),

    getMonitoringMassPostingSnapshot: builder.query<
      {
        account: string;
        items: Array<{
          avito_ad_id: string;
          title: string;
          category: string;
          address?: string | null;
          city?: string | null;
          xl: boolean;
          promoted: boolean;
          click: number | null;
          limit: number | null;
          comment: string | null;
          updated_at: string | null;
        }>;
        count: number;
      },
      { account: string }
    >({
      query: ({ account }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}`,
      }),
      providesTags: [{ type: "Monitoring", id: "mass-posting-snapshot" }],
    }),

    getMonitoringCatalogClickLimit: builder.query<
      {
        catalog: "samsung" | "xiaomi" | "extra_products";
        account: string;
        items: Record<
          string,
          {
            avito_id: string;
            click: number | null;
            limit: number | null;
            desired_position?: number | null;
            comment?: string | null;
            xl?: boolean | null;
            promoted?: boolean | null;
            apply_status?: string | null;
            apply_error?: string | null;
            actual_click?: number | null;
            actual_limit?: number | null;
            actual_promoted?: boolean | null;
          }
        >;
      },
      { catalog: "samsung" | "xiaomi" | "extra_products"; account: string }
    >({
      query: ({ catalog, account }) => ({
        url: `/monitoring/catalog/${encodeURIComponent(catalog)}/click-limit`,
        params: { account },
      }),
      providesTags: ["Monitoring"],
    }),

    updateMonitoringCatalogClickLimit: builder.mutation<
      {
        avito_id: string;
        click: number | null;
        limit: number | null;
        desired_position?: number | null;
        xl?: boolean | null;
        promoted?: boolean | null;
        comment?: string | null;
      },
      {
        catalog: "samsung" | "xiaomi" | "extra_products";
        account: string;
        avito_id: string;
        click?: number | null;
        limit?: number | null;
        desired_position?: number | null;
        xl?: boolean | null;
        promoted?: boolean | null;
        comment?: string | null;
      }
    >({
      query: ({ catalog, account, ...body }) => ({
        url: `/monitoring/catalog/${encodeURIComponent(catalog)}/click-limit`,
        method: "POST",
        params: { account },
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    updateMonitoringCatalogClickLimitBulk: builder.mutation<
      {
        catalog: "samsung" | "xiaomi" | "extra_products";
        account: string;
        summary: {
          total: number;
          updated: number;
          noop: number;
          error: number;
        };
        results: Array<{
          avito_id: string;
          status: string;
          click?: number | null;
          limit?: number | null;
          desired_position?: number | null;
          xl?: boolean | null;
          promoted?: boolean | null;
          comment?: string | null;
          error?: string | null;
        }>;
      },
      {
        catalog: "samsung" | "xiaomi" | "extra_products";
        account: string;
        updates: Array<{
          avito_id: string;
          click?: number | null;
          limit?: number | null;
          desired_position?: number | null;
          xl?: boolean | null;
          promoted?: boolean | null;
          comment?: string | null;
        }>;
      }
    >({
      query: ({ catalog, account, updates }) => ({
        url: `/monitoring/catalog/${encodeURIComponent(catalog)}/click-limit/bulk`,
        method: "POST",
        params: { account },
        body: { updates },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringMassPostingPreview: builder.query<
      {
        account: string;
        items: Array<{
          avito_ad_id: string;
          title: string;
          category: string;
          xl: boolean;
          promoted: boolean;
          click: number | null;
          limit: number | null;
          comment: string | null;
        }>;
        total: number;
        promoted: number;
        stored_promoted_ids: string[];
      },
      { account: string }
    >({
      query: ({ account }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}/preview`,
      }),
      providesTags: ["Monitoring"],
    }),

    saveMonitoringMassPosting: builder.mutation<
      {
        account: string;
        inserted: number;
        deleted: boolean;
      },
      {
        account: string;
        items: Array<{
          avito_ad_id: string;
          title?: string | null;
          category?: string | null;
          address?: string | null;
          xl?: boolean;
          promoted?: boolean;
          click?: number | null;
          limit?: number | null;
          comment?: string | null;
        }>;
      }
    >({
      query: ({ account, items }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}`,
        method: "POST",
        body: { items },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    updateMonitoringMassPostingClickLimit: builder.mutation<
      {
        avito_id: string;
        click: number | null;
        limit: number | null;
        xl: boolean | null;
        promoted: boolean | null;
        comment: string | null;
      },
      {
        account: string;
        avito_id: string;
        click?: number | null;
        limit?: number | null;
        xl?: boolean | null;
        promoted?: boolean | null;
        comment?: string | null;
      }
    >({
      query: ({ account, ...body }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}/click-limit`,
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "Monitoring", id: "mass-posting-snapshot" },
        { type: "Monitoring", id: "today-summary" },
      ],
    }),

    updateMonitoringMassPostingClickLimitBulk: builder.mutation<
      {
        account: string;
        summary: {
          total: number;
          updated: number;
          noop: number;
          error: number;
        };
        results: Array<{
          avito_id: string;
          status: string;
          click?: number | null;
          limit?: number | null;
          xl?: boolean | null;
          promoted?: boolean | null;
          comment?: string | null;
          error?: string | null;
        }>;
      },
      {
        account: string;
        updates: Array<{
          avito_id: string;
          click?: number | null;
          limit?: number | null;
          xl?: boolean | null;
          promoted?: boolean | null;
          comment?: string | null;
        }>;
      }
    >({
      query: ({ account, updates }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}/click-limit/bulk`,
        method: "POST",
        body: { updates },
      }),
      invalidatesTags: [
        { type: "Monitoring", id: "mass-posting-snapshot" },
        { type: "Monitoring", id: "today-summary" },
      ],
    }),

    getMonitoringMassPostingPromotionSpace: builder.query<
      {
        account: string;
        target_per_group: number;
        groups: Array<{
          group_key: string;
          address_key: string | null;
          address: string | null;
          category: string | null;
          target_per_group: number;
          total_candidates: number;
          selected_count: number;
          candidates: Array<{
            avito_ad_id: string;
            title: string | null;
            category: string | null;
            address: string | null;
            photo_key: string | null;
            photo_url: string | null;
            date_end?: string | null;
            photo_duplicate_on_address: boolean;
            views: number | null;
            impressions: number | null;
            contacts: number | null;
            spends: number | null;
            price: number | null;
            promoted: boolean;
            xl: boolean;
            click: number | null;
            limit: number | null;
            comment: string | null;
            recommended: boolean;
          }>;
        }>;
        total: number;
        promoted: number;
        stored_promoted_ids: string[];
      },
      { account: string; target_per_group?: number }
    >({
      query: ({ account, target_per_group }) => {
        const params = new URLSearchParams();
        if (target_per_group !== undefined && target_per_group !== 15) {
          params.set("target_per_group", String(target_per_group));
        }
        const qs = params.toString();
        return qs
          ? `/monitoring/mass-posting/${encodeURIComponent(account)}/promotion-space?${qs}`
          : `/monitoring/mass-posting/${encodeURIComponent(account)}/promotion-space`;
      },
      providesTags: [{ type: "Monitoring", id: "mass-posting-snapshot" }],
    }),

    applyMonitoringMassPostingPromotionSpace: builder.mutation<
      {
        account: string;
        inserted: number;
        updated: number;
        disabled: number;
      },
      {
        account: string;
        apply_mode: "preserve" | "reconcile";
        items: Array<{
          avito_ad_id: string;
          title?: string | null;
          category?: string | null;
          address?: string | null;
          photo_key?: string | null;
          promoted?: boolean;
          xl?: boolean;
          click?: number | null;
          limit?: number | null;
          comment?: string | null;
        }>;
      }
    >({
      query: ({ account, items, apply_mode }) => ({
        url: `/monitoring/mass-posting/${encodeURIComponent(account)}/promotion-space/apply`,
        method: "POST",
        body: { updates: items, apply_mode },
      }),
      invalidatesTags: [
        { type: "Monitoring", id: "mass-posting-snapshot" },
        { type: "Monitoring", id: "today-summary" },
      ],
    }),

    getMonitoringCpxSnapshot: builder.query<
      MonitoringCpxSnapshotResponse,
      { account: string; ad_ids: string[]; include_ladder?: boolean }
    >({
      query: (body) => ({
        url: "/monitoring/promotion/cpx/snapshot",
        method: "POST",
        body,
      }),
      providesTags: ["MonitoringCpx", "Monitoring"],
    }),

    getMonitoringCpxAccountSummary: builder.query<
      MonitoringCpxAccountSummary,
      { account: string }
    >({
      query: ({ account }) =>
        `/monitoring/promotion/cpx/account-summary?account=${encodeURIComponent(account)}`,
      providesTags: ["MonitoringCpx", "Monitoring"],
    }),

    // ==================== AUTOAZ ====================
    runAutoAZ: builder.mutation<
      {
        success: boolean;
        task_id: string;
        message: string;
      },
      {
        spreadsheet_id: string;
        operations: {
          update_dates: boolean;
          unique_descriptions: boolean;
          update_image_urls: boolean;
          update_contact_phone?: boolean;
          description_mode?: "ai" | "manual";
          image_urls_mode?: "auto" | "manual";
        };
        sheets: string[];
        folder_names?: Record<string, string>;
        id_columns?: string[];
        contact_phone?: string;
        custom_descriptions?: Record<string, string>;
        custom_image_urls?: Record<string, string[]>;
      }
    >({
      query: (body) => ({
        url: "/autoload/autoaz",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Autoload"],
    }),

    getAutoAZTaskStatus: builder.query<
      {
        status: string;
        stage: string;
        message: string;
        progress: number;
        spreadsheet_id?: string;
        error?: string;
        output?: string;
        updated_at?: string;
      },
      string
    >({
      query: (taskId) =>
        `/autoload/autoaz/status/${encodeURIComponent(taskId)}`,
      providesTags: (result, error, taskId) => [
        { type: "Autoload", id: `autoaz-${taskId}` },
      ],
    }),

    cancelAutoAZ: builder.mutation<
      { success: boolean; task_id: string; message: string },
      string
    >({
      query: (taskId) => ({
        url: `/autoload/autoaz/${encodeURIComponent(taskId)}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["Autoload"],
    }),

    getSheetDescriptions: builder.query<
      Record<string, string>,
      {
        spreadsheet_id: string;
        sheets: string[];
      }
    >({
      query: (params) => ({
        url: "/autoload/autoaz/descriptions",
        method: "POST",
        body: params,
      }),
    }),

    getSheetImageUrls: builder.query<
      Record<string, string[]>,
      {
        spreadsheet_id: string;
        sheets: string[];
      }
    >({
      query: (params) => ({
        url: "/autoload/autoaz/image-urls",
        method: "POST",
        body: params,
      }),
    }),

    addIphoneModel: builder.mutation<
      {
        success: boolean;
        message: string;
        spreadsheet_id: string;
        worksheet: string;
        rows_added: number;
        start_row: number;
        name_columns_found: number;
        cells_updated: number;
        checkboxes_disabled_ah?: number;
        checkboxes_disabled_bn?: number;
        sheets_updated?: number;
        listing_rows_added?: number;
        sheet_results?: Array<{
          sheet_name: string;
          status: "success" | "skipped" | "error";
          message: string;
          start_row?: number;
          rows_written?: number;
        }>;
      },
      {
        spreadsheet_id: string;
        model_name?: string;
        storage_variants?: string[];
        correction_mode?: boolean;
        device_color_pairs?: Array<{ device: string; color: string }>;
        listing_rows?: Array<{
          title: string;
          model: string;
          memory_size: string;
          ram_size: string;
          color: string;
          path_color?: string;
        }>;
      }
    >({
      query: (body) => ({
        url: "/autoload/autoaz/add-iphone-model",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Autoload"],
    }),

    writeAnalyticsBlock: builder.mutation<
      {
        success: boolean;
        message: string;
        rows_written: number;
        analytics_start_row: number;
      },
      {
        spreadsheet_id: string;
        model_name: string;
        storage_variants: string[];
      }
    >({
      query: (body) => ({
        url: "/autoload/autoaz/write-analytics-block",
        method: "POST",
        body,
      }),
    }),

    updateMonitoringClickLimitBulk: builder.mutation<
      {
        account: string;
        sheet_name: string;
        summary: {
          total: number;
          deduplicated: number;
          updated: number;
          noop: number;
          not_found: number;
          error: number;
        };
        results: Array<{
          avito_id: string;
          status: "updated" | "noop" | "not_found" | "error";
          click?: number | null;
          limit?: number | null;
          desired_position?: number | null;
          comment?: string | null;
          xl?: boolean | null;
          promoted?: boolean | null;
          error?: string | null;
        }>;
      },
      {
        account: string;
        transfer_xl?: boolean;
        updates: Array<{
          avito_id: string;
          click?: number | null;
          limit?: number | null;
          desired_position?: number | null;
          comment?: string | null;
          xl?: boolean | null;
          promoted?: boolean | null;
        }>;
      }
    >({
      query: (body) => ({
        url: "/monitoring/click-limit/bulk",
        method: "POST",
        body,
      }),
      async onQueryStarted({ account, updates }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          analyticsApi.util.updateQueryData(
            "getMonitoringClickLimit",
            { account, force: false },
            (draft: any) => {
              if (!draft || !draft.items || !Array.isArray(updates)) return;
              updates.forEach((item) => {
                const key = String(item?.avito_id || "").trim();
                if (!key) return;
                if (!draft.items[key]) {
                  draft.items[key] = {
                    avito_id: key,
                    click: null,
                    limit: null,
                    desired_position: null,
                    comment: null,
                    xl: null,
                    promoted: null,
                  };
                }
                if (Object.prototype.hasOwnProperty.call(item, "click"))
                  draft.items[key].click = item?.click ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "limit"))
                  draft.items[key].limit = item?.limit ?? null;
                if (
                  Object.prototype.hasOwnProperty.call(item, "desired_position")
                )
                  draft.items[key].desired_position =
                    item?.desired_position ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "comment"))
                  draft.items[key].comment = item?.comment ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "xl"))
                  draft.items[key].xl = item?.xl ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "promoted"))
                  draft.items[key].promoted = item?.promoted ?? null;
              });
            },
          ),
        );
        const workspacePatch = dispatch(
          analyticsApi.util.updateQueryData(
            "getMonitoringWorkspaceSnapshot",
            { account },
            (draft: any) => {
              if (!draft || !draft.click_limit_by_id || !Array.isArray(updates))
                return;
              updates.forEach((item) => {
                const key = String(item?.avito_id || "").trim();
                if (!key) return;
                const row =
                  draft.click_limit_by_id[key] ||
                  (draft.click_limit_by_id[key] = {});
                if (Object.prototype.hasOwnProperty.call(item, "click"))
                  row.click = item?.click ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "limit"))
                  row.limit = item?.limit ?? null;
                if (
                  Object.prototype.hasOwnProperty.call(item, "desired_position")
                )
                  row.desired_position = item?.desired_position ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "comment"))
                  row.comment = item?.comment ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "xl"))
                  row.xl = item?.xl ?? null;
                if (Object.prototype.hasOwnProperty.call(item, "promoted"))
                  row.promoted = item?.promoted ?? null;
              });
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            analyticsApi.util.updateQueryData(
              "getMonitoringClickLimit",
              { account, force: false },
              (draft: any) => {
                if (!draft || !draft.items) return;
                const results = Array.isArray((data as any)?.results)
                  ? (data as any).results
                  : [];
                results.forEach((res: any) => {
                  const key = String(res?.avito_id || "").trim();
                  if (!key) return;
                  if (!draft.items[key]) {
                    draft.items[key] = {
                      avito_id: key,
                      click: null,
                      limit: null,
                      desired_position: null,
                      comment: null,
                      xl: null,
                      promoted: null,
                    };
                  }
                  if (Object.prototype.hasOwnProperty.call(res, "click"))
                    draft.items[key].click = res?.click ?? null;
                  if (Object.prototype.hasOwnProperty.call(res, "limit"))
                    draft.items[key].limit = res?.limit ?? null;
                  if (
                    Object.prototype.hasOwnProperty.call(
                      res,
                      "desired_position",
                    )
                  )
                    draft.items[key].desired_position =
                      res?.desired_position ?? null;
                  if (Object.prototype.hasOwnProperty.call(res, "comment"))
                    draft.items[key].comment = res?.comment ?? null;
                  if (Object.prototype.hasOwnProperty.call(res, "xl"))
                    draft.items[key].xl = res?.xl ?? null;
                  if (Object.prototype.hasOwnProperty.call(res, "promoted"))
                    draft.items[key].promoted = res?.promoted ?? null;
                });
              },
            ),
          );
        } catch {
          patchResult.undo();
          workspacePatch.undo();
        }
      },
    }),

    updateMonitoringClickLimit: builder.mutation<
      {
        avito_id: string;
        click: number | null;
        limit: number | null;
        desired_position?: number | null;
        comment?: string | null;
        xl?: boolean | null;
        promoted?: boolean | null;
      },
      {
        account: string;
        avito_id: string;
        click?: number | null;
        limit?: number | null;
        desired_position?: number | null;
        comment?: string | null;
        xl?: boolean | null;
        promoted?: boolean | null;
      }
    >({
      query: (body) => ({
        url: "/monitoring/click-limit",
        method: "POST",
        body,
      }),
      async onQueryStarted(
        {
          account,
          avito_id,
          click,
          limit,
          desired_position,
          comment,
          xl,
          promoted,
        },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          analyticsApi.util.updateQueryData(
            "getMonitoringClickLimit",
            { account, force: false },
            (draft: any) => {
              if (!draft || !draft.items) return;
              const key = String(avito_id);
              if (!draft.items[key]) {
                draft.items[key] = {
                  avito_id: key,
                  click: null,
                  limit: null,
                  desired_position: null,
                  comment: null,
                  xl: null,
                  promoted: null,
                };
              }
              if (click !== undefined) draft.items[key].click = click;
              if (limit !== undefined) draft.items[key].limit = limit;
              if (desired_position !== undefined)
                draft.items[key].desired_position = desired_position;
              if (comment !== undefined) draft.items[key].comment = comment;
              if (xl !== undefined) draft.items[key].xl = xl;
              if (promoted !== undefined) draft.items[key].promoted = promoted;
            },
          ),
        );
        const workspacePatch = dispatch(
          analyticsApi.util.updateQueryData(
            "getMonitoringWorkspaceSnapshot",
            { account },
            (draft: any) => {
              if (!draft || !draft.click_limit_by_id) return;
              const key = String(avito_id);
              const row =
                draft.click_limit_by_id[key] ||
                (draft.click_limit_by_id[key] = {});
              if (click !== undefined) row.click = click;
              if (limit !== undefined) row.limit = limit;
              if (desired_position !== undefined)
                row.desired_position = desired_position;
              if (comment !== undefined) row.comment = comment;
              if (xl !== undefined) row.xl = xl;
              if (promoted !== undefined) row.promoted = promoted;
            },
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            analyticsApi.util.updateQueryData(
              "getMonitoringClickLimit",
              { account, force: false },
              (draft: any) => {
                if (!draft || !draft.items) return;
                const key = String(data?.avito_id ?? avito_id);
                if (!draft.items[key]) {
                  draft.items[key] = {
                    avito_id: key,
                    click: null,
                    limit: null,
                    desired_position: null,
                    comment: null,
                    xl: null,
                    promoted: null,
                  };
                }
                if (Object.prototype.hasOwnProperty.call(data || {}, "click")) {
                  draft.items[key].click = (data as any)?.click ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(data || {}, "limit")) {
                  draft.items[key].limit = (data as any)?.limit ?? null;
                }
                if (
                  Object.prototype.hasOwnProperty.call(
                    data || {},
                    "desired_position",
                  )
                ) {
                  draft.items[key].desired_position =
                    (data as any)?.desired_position ?? null;
                }
                if (
                  Object.prototype.hasOwnProperty.call(data || {}, "comment")
                ) {
                  draft.items[key].comment = (data as any)?.comment ?? null;
                }
                if (Object.prototype.hasOwnProperty.call(data || {}, "xl")) {
                  draft.items[key].xl = (data as any)?.xl ?? null;
                }
                if (
                  Object.prototype.hasOwnProperty.call(data || {}, "promoted")
                ) {
                  draft.items[key].promoted = (data as any)?.promoted ?? null;
                }
              },
            ),
          );
        } catch {
          patchResult.undo();
          workspacePatch.undo();
        }
      },
    }),

    getMonitoringActionHistory: builder.query<
      any,
      {
        account: string;
        date_from?: string;
        date_to?: string;
        avito_id?: string;
        action_type?: string;
        source?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: (params) => ({
        url: "/monitoring/action-history",
        params,
      }),
      transformResponse: (response: {
        account: string;
        total: number;
        items: any[];
      }) => {
        return {
          ...response,
          items: response.items.map((item) => ({
            id: item.id,
            created_at: item.timestamp,
            item_id: item.avito_id,
            item_title: item.item_title || "",
            field: item.field_name || item.action_type,
            old_value: item.old_value,
            new_value: item.new_value,
            user_id: item.user_id,
            source: item.source,
          })),
        };
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringActionHistoryTodaySummary: builder.query<
      MonitoringActionHistoryTodaySummaryResponse,
      { account: string }
    >({
      query: ({ account }) => ({
        url: "/monitoring/action-history/today-summary",
        params: { account },
      }),
      providesTags: [{ type: "Monitoring", id: "today-summary" }],
    }),

    getMonitoringActionHistorySummary: builder.query<
      MonitoringActionHistorySummaryResponse,
      { account: string; date_from?: string; date_to?: string }
    >({
      query: (params) => ({
        url: "/monitoring/action-history/summary",
        params,
      }),
      providesTags: [{ type: "Monitoring", id: "action-history-summary" }],
    }),

    getMonitoringActionHistoryLatestByAd: builder.query<
      MonitoringLatestActionResponse,
      { account: string; avito_id: string }
    >({
      query: (params) => ({
        url: "/monitoring/action-history/latest-by-ad",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPromotionDailySummary: builder.query<
      {
        account: string;
        date_range: { from: string; to: string };
        daily: Array<{
          date: string;
          total_changes: number;
          unique_ads: number;
          total_up: number;
          total_down: number;
        }>;
      },
      { account: string; date_from: string; date_to: string }
    >({
      query: (params) => ({
        url: "/monitoring/action-history/daily-summary",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPromotionDayAdsDetail: builder.query<
      {
        account: string;
        date: string;
        ads: Array<{
          avito_id: string;
          item_title?: string;
          changes_count: number;
          changes: {
            timestamp: string;
            action_type: string;
            field_name?: string;
            old_value?: string;
            new_value?: string;
            source: string;
          }[];
        }>;
      },
      { account: string; date: string }
    >({
      query: (params) => ({
        url: "/monitoring/action-history/day-ads-detail",
        params,
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPlanningConfig: builder.query<
      {
        excluded_accounts: string[];
        promoted_ads_per_model: number;
        schedule_enabled: boolean;
        schedule_times: string[];
        last_run_at: string | null;
        last_run_summary: Record<string, any> | null;
      },
      void
    >({
      query: () => "/monitoring/planning/config",
      providesTags: ["Monitoring"],
    }),

    setMonitoringPlanningConfig: builder.mutation<
      {
        excluded_accounts: string[];
        schedule_enabled: boolean;
        schedule_times: string[];
        last_run_at: string | null;
        last_run_summary: Record<string, any> | null;
      },
      {
        excluded_accounts?: string[];
        promoted_ads_per_model?: number;
        schedule_enabled?: boolean;
        schedule_times?: string[];
      }
    >({
      query: (body) => ({
        url: "/monitoring/planning/config",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    // ==================== MONITORING BIDDER ====================

    getMonitoringBidderConfig: builder.query<
      {
        account_name: string;
        catalog: "iphone" | "samsung" | "xiaomi";
        enabled: boolean;
        xl_enabled: boolean;
        non_xl_enabled: boolean;
        config_json: string;
        updated_at: string | null;
      },
      { account: string; catalog?: "iphone" | "samsung" | "xiaomi" }
    >({
      query: ({ account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/config/${encodeURIComponent(account)}`,
        params: { catalog },
      }),
      providesTags: ["Monitoring"],
    }),

    updateMonitoringBidderConfig: builder.mutation<
      {
        account_name: string;
        catalog: "iphone" | "samsung" | "xiaomi";
        enabled: boolean;
        xl_enabled: boolean;
        non_xl_enabled: boolean;
        config_json: string;
        updated_at: string | null;
      },
      {
        account: string;
        config_json: string;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, config_json, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/config/${encodeURIComponent(account)}`,
        method: "PUT",
        params: { catalog },
        body: { config_json },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getMonitoringBidderTemplates: builder.query<
      {
        templates: Array<{
          id: number;
          name: string;
          config_json: string;
          created_at: string;
        }>;
      },
      void
    >({
      query: () => "/monitoring/bidder/templates",
      providesTags: ["Monitoring"],
    }),

    createMonitoringBidderTemplate: builder.mutation<
      {
        id: number;
        name: string;
        config_json: string;
        created_at: string;
      },
      { name: string; config_json: string }
    >({
      query: (body) => ({
        url: "/monitoring/bidder/templates",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    deleteMonitoringBidderTemplate: builder.mutation<void, number>({
      query: (template_id) => ({
        url: `/monitoring/bidder/templates/${template_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    applyMonitoringBidderTemplate: builder.mutation<
      {
        account_name: string;
        catalog: "iphone" | "samsung" | "xiaomi";
        enabled: boolean;
        xl_enabled: boolean;
        non_xl_enabled: boolean;
        config_json: string;
        updated_at: string | null;
      },
      {
        template_id: number;
        account: string;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ template_id, account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/templates/${template_id}/apply/${encodeURIComponent(account)}`,
        method: "POST",
        params: { catalog },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    runMonitoringBidderDryRun: builder.mutation<
      {
        account: string;
        sheet_name: string;
        yesterday_key: string;
        date_key: string | null;
        is_simulation: boolean;
        total: number;
        will_update: number;
        skipped: number;
        errors: number;
        no_data: number;
        position_51_detected: boolean;
        position_51_blocked: boolean;
        position_51_warning: string | null;
        active_promoted_count: number;
        active_with_51_pair: number;
        items: Array<{
          avito_id: string;
          title: string;
          position: number | null;
          is_xl: boolean;
          current_click: number | null;
          current_limit: number | null;
          new_click: number | null;
          new_limit: number | null;
          matched_from: number | null;
          matched_to: number | null;
          status: string;
          error: string | null;
          explain: string | null;
          decision_reason: string | null;
          detected_hour_pair: string | null;
          ad_status: string | null;
          inactive_warning: string | null;
          account_wide_warning: string | null;
        }>;
      },
      {
        account: string;
        date?: string | null;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, date, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/dry-run/${encodeURIComponent(account)}`,
        method: "POST",
        body: { date: date ?? null, catalog },
      }),
    }),

    getMonitoringBidderAvailableDates: builder.query<
      {
        account: string;
        account_name: string;
        sheet_name: string;
        yesterday_key: string;
        today_key: string;
        total_promoted: number;
        dates: Array<{
          date: string;
          items_with_position: number;
          total_promoted: number;
          is_yesterday: boolean;
        }>;
      },
      { account: string }
    >({
      query: ({ account }) => ({
        url: `/monitoring/bidder/available-dates/${encodeURIComponent(account)}`,
      }),
    }),

    runMonitoringBidderSimulateCompare: builder.mutation<
      {
        account: string;
        sheet_name: string;
        date_key: string;
        differs_count: number;
        summary_a: {
          total: number;
          will_update: number;
          skipped: number;
          no_data: number;
          errors: number;
        };
        summary_b: {
          total: number;
          will_update: number;
          skipped: number;
          no_data: number;
          errors: number;
        };
        items: Array<{
          avito_id: string;
          title: string;
          position: number | null;
          is_xl: boolean;
          current_click: number | null;
          current_limit: number | null;
          a: {
            new_click: number | null;
            new_limit: number | null;
            matched_from: number | null;
            matched_to: number | null;
            status: string;
            explain: string | null;
          };
          b: {
            new_click: number | null;
            new_limit: number | null;
            matched_from: number | null;
            matched_to: number | null;
            status: string;
            explain: string | null;
          };
          differs: boolean;
        }>;
      },
      {
        account: string;
        date?: string | null;
        config_a_json: string;
        config_b_json: string;
      }
    >({
      query: ({ account, date, config_a_json, config_b_json }) => ({
        url: `/monitoring/bidder/simulate-compare/${encodeURIComponent(account)}`,
        method: "POST",
        body: { date: date ?? null, config_a_json, config_b_json },
      }),
    }),

    runMonitoringBidderApply: builder.mutation<
      {
        account: string;
        sheet_name: string;
        run_id: number;
        total: number;
        updated: number;
        skipped: number;
        errors: number;
        position_51_warning: string | null;
        position_51_blocked: boolean;
      },
      { account: string; catalog?: "iphone" | "samsung" | "xiaomi" }
    >({
      query: ({ account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/apply/${encodeURIComponent(account)}`,
        method: "POST",
        body: { catalog },
      }),
      invalidatesTags: [
        "Monitoring",
        { type: "Monitoring", id: "today-summary" },
      ],
    }),

    getMonitoringBidderOverview: builder.query<
      MonitoringBidderOverviewResponse,
      void
    >({
      query: () => "/monitoring/bidder/runs/overview",
      providesTags: ["Monitoring"],
      // The server publishes a durable run snapshot. Keep it in the client as
      // well; polling every minute only made an already static report flicker.
      keepUnusedDataFor: 600,
    }),

    getMonitoringAccountWorkbench: builder.query<
      {
        selected_account_names: string[];
        watchers_by_account: Record<
          string,
          Array<{ user_id: number; display_name: string }>
        >;
      },
      void
    >({
      query: () => "/monitoring/bidder/workbench",
      providesTags: ["MonitoringWorkbench"],
    }),

    saveMonitoringAccountWorkbench: builder.mutation<
      {
        selected_account_names: string[];
        watchers_by_account: Record<
          string,
          Array<{ user_id: number; display_name: string }>
        >;
      },
      {
        account_names: string[];
      }
    >({
      query: (body) => ({
        url: "/monitoring/bidder/workbench",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MonitoringWorkbench"],
    }),

    getMonitoringPosition51Summary: builder.query<
      MonitoringPosition51Summary,
      { account: string; catalog?: "iphone" | "samsung" | "xiaomi" }
    >({
      query: ({ account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/position-51/summary/${encodeURIComponent(account)}`,
        params: { catalog },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringPosition51Replay: builder.query<
      MonitoringPosition51Replay,
      { account: string; catalog?: "iphone" | "samsung" | "xiaomi" }
    >({
      query: ({ account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/position-51/replay/${encodeURIComponent(account)}`,
        params: { catalog },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderRuns: builder.query<
      {
        runs: Array<{
          id: number;
          account_name: string;
          catalog: "iphone" | "samsung" | "xiaomi";
          ran_at: string;
          status: string;
          total_items: number;
          updated_items: number;
          error_items: number;
          summary_json: string | null;
        }>;
      },
      {
        account: string;
        limit?: number;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, limit = 10, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/runs/${encodeURIComponent(account)}`,
        params: { limit, catalog },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderRunItems: builder.query<
      {
        run_id: number;
        items: Array<{
          avito_id: string;
          position: number | null;
          is_xl: boolean;
          current_click: number | null;
          current_limit: number | null;
          new_click: number | null;
          new_limit: number | null;
          matched_from: number | null;
          matched_to: number | null;
          status: string;
          error: string | null;
        }>;
      },
      {
        account: string;
        run_id: number;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, run_id, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/runs/${encodeURIComponent(account)}/${run_id}/items`,
        params: { catalog },
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsSummary: builder.query<
      BidderEffectivenessSummary,
      BidderEffectivenessFilters
    >({
      query: (filters) => ({
        url: "/monitoring/bidder/analytics/summary",
        params: bidderAnalyticsParams(filters),
      }),
      transformResponse: (raw: any): BidderEffectivenessSummary => {
        const eligible = Number(raw?.eligible_events || 0);
        const total = Number(raw?.total_events || 0);
        return {
          kpis: {
            decisions: total,
            changed: total,
            improved_share: eligible
              ? (Number(raw.improved || 0) / eligible) * 100
              : null,
            worsened_share: eligible
              ? (Number(raw.worsened || 0) / eligible) * 100
              : null,
            median_position_delta: raw?.median_delta_6h ?? null,
            median_time_to_effect_hours:
              raw?.median_time_to_effect_hours ?? null,
            cost_per_position: raw?.median_cost_per_position ?? null,
            insufficient_data_share: total
              ? ((Number(raw.insufficient || 0) + Number(raw.confounded || 0)) /
                  total) *
                100
              : null,
          },
          quality: {
            coverage_pct: total ? (eligible / total) * 100 : null,
            measured_events: eligible,
            insufficient_events: Number(raw?.insufficient || 0),
            confounded_events: Number(raw?.confounded || 0),
            last_sample_at: null,
          },
          outcome_counts: {
            improved: Number(raw?.improved || 0),
            unchanged: Number(raw?.stable || 0),
            worsened: Number(raw?.worsened || 0),
            insufficient_data: Number(raw?.insufficient || 0),
            confounded: Number(raw?.confounded || 0),
          },
          direction_counts: {
            increase: Number(raw?.direction_counts?.increase || 0),
            decrease: Number(raw?.direction_counts?.decrease || 0),
            unchanged: Number(raw?.direction_counts?.none || 0),
          },
        };
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsEvents: builder.query<
      BidderEventsResponse,
      BidderEffectivenessFilters
    >({
      query: (filters) => ({
        url: "/monitoring/bidder/analytics/events",
        params: {
          ...bidderAnalyticsParams(filters),
          ...(filters.outcome
            ? {
                outcome:
                  filters.outcome === "unchanged"
                    ? "stable"
                    : filters.outcome === "insufficient_data"
                      ? "insufficient"
                      : filters.outcome,
              }
            : {}),
          ...(filters.direction
            ? {
                direction:
                  filters.direction === "unchanged"
                    ? "none"
                    : filters.direction,
              }
            : {}),
          ...(filters.rule_id ? { rule_id: filters.rule_id } : {}),
          ...(filters.reliable_only === undefined
            ? {}
            : { reliable_only: filters.reliable_only }),
          ...(filters.query ? { query: filters.query } : {}),
          limit: filters.page_size,
          offset: (filters.page - 1) * filters.page_size,
        },
      }),
      transformResponse: (raw: any): BidderEventsResponse => ({
        items: Array.isArray(raw?.items)
          ? raw.items.map(normalizeBidderEvent)
          : [],
        total: Number(raw?.total || 0),
        page:
          Math.floor(
            Number(raw?.offset || 0) / Math.max(1, Number(raw?.limit || 25)),
          ) + 1,
        page_size: Number(raw?.limit || 25),
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsTimeline: builder.query<
      BidderTimelineResponse,
      { event_id: string | number }
    >({
      query: ({ event_id }) =>
        `/monitoring/bidder/analytics/events/${encodeURIComponent(String(event_id))}/timeline`,
      transformResponse: (raw: any): BidderTimelineResponse => {
        const event = normalizeBidderEvent(raw?.event || {});
        const samplePoints = (
          Array.isArray(raw?.samples) ? raw.samples : []
        ).map((sample: any) => ({
          timestamp: String(sample?.timestamp || ""),
          position: sample?.position === 0 ? null : (sample?.position ?? null),
          click: null,
          limit: null,
          marker: null,
          marker_label: null,
        }));
        const actionPoints = (
          Array.isArray(raw?.actions) ? raw.actions : []
        ).map((action: any) => ({
          timestamp: String(action?.timestamp || ""),
          position: null,
          click:
            action?.field_name === "click" ? Number(action?.new_value) : null,
          limit:
            action?.field_name === "limit" ? Number(action?.new_value) : null,
          marker:
            action?.source === "bidder"
              ? "bidder"
              : action?.field_name === "xl"
                ? "xl_rotation"
                : "manual",
          marker_label: action?.field_name || null,
        }));
        return {
          event,
          points: [...samplePoints, ...actionPoints]
            .filter((point) => point.timestamp)
            .sort((a, b) => a.timestamp.localeCompare(b.timestamp)),
        };
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsRules: builder.query<
      BidderRulesResponse,
      BidderEffectivenessFilters
    >({
      query: (filters) => ({
        url: "/monitoring/bidder/analytics/rules",
        params: bidderAnalyticsParams(filters),
      }),
      transformResponse: (raw: any): BidderRulesResponse => ({
        items: (Array.isArray(raw?.items) ? raw.items : []).map(
          (rule: any) => ({
            rule_id: String(rule.rule_key),
            label: `Позиции ${rule.matched_from ?? "—"}–${rule.matched_to ?? "—"} · ${rule.direction === "increase" ? "повышение" : rule.direction === "decrease" ? "понижение" : "без изменения"}`,
            group: rule.is_xl ? "xl" : "non_xl",
            sample_size: Number(rule.valid || 0),
            improved_share: rule.valid
              ? (Number(rule.improved || 0) / Number(rule.valid)) * 100
              : null,
            worsened_share: rule.valid
              ? (Number(rule.worsened || 0) / Number(rule.valid)) * 100
              : null,
            median_position_delta: rule.median_delta_h6 ?? null,
            median_time_to_effect_hours:
              rule.median_time_to_effect_hours ?? null,
            cost_per_position: rule.median_cost_per_position ?? null,
            persistence: rule.median_persistence ?? null,
            confidence: (["high", "medium", "low"].includes(rule.confidence)
              ? rule.confidence
              : "low") as BidderConfidence,
          }),
        ),
      }),
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsAnomalies: builder.query<
      BidderAnomaliesResponse,
      BidderEffectivenessFilters
    >({
      query: (filters) => ({
        url: "/monitoring/bidder/analytics/anomalies",
        params: bidderAnalyticsParams(filters),
      }),
      transformResponse: (raw: any): BidderAnomaliesResponse => {
        const items = (Array.isArray(raw?.items) ? raw.items : []).map(
          (item: any, index: number) => ({
            id: `${item.type}-${item.event_id ?? index}`,
            type: String(item.type || "unknown"),
            severity:
              item.severity === "high"
                ? ("high" as const)
                : ("medium" as const),
            avito_id: String(item.avito_id || ""),
            title: String(item.item_title || ""),
            detected_at: String(item.detected_at || ""),
            description: `${
              item.type === "no_effect"
                ? "Ставка выросла, но позиция заметно не изменилась."
                : item.type === "harmful"
                  ? "После повышения ставки позиция ухудшилась."
                  : item.type === "data_gap"
                    ? "После решения не хватает замеров для оценки."
                    : item.type === "overshoot"
                      ? "Ставка повышена при уже очень высокой позиции."
                      : item.type === "oscillation"
                        ? "Ставка несколько раз менялась в противоположных направлениях."
                        : "Обнаружено нетипичное поведение."
            }${[item.model_name, item.city].filter(Boolean).length ? ` · ${[item.model_name, item.city].filter(Boolean).join(", ")}` : ""}`,
            occurrences: Number(item?.details?.changes || 1),
            event_id: item.event_id ?? null,
          }),
        );
        return { items, total: items.length };
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderOperationalAnomalies: builder.query<
      BidderOperationalAnomaliesResponse,
      {
        account: string;
        xl?: boolean;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, xl, catalog = "iphone" }) => ({
        url: "/monitoring/bidder/analytics/anomalies/operational",
        params: {
          account,
          catalog,
          ...(xl === undefined ? {} : { is_xl: xl }),
        },
      }),
      transformResponse: (raw: any): BidderOperationalAnomaliesResponse => {
        const items = (Array.isArray(raw?.items) ? raw.items : []).map(
          (item: any, index: number) => ({
            id: `operational-${item.event_id ?? index}`,
            type: String(item.type || "direction_reversal"),
            severity:
              item.severity === "medium"
                ? ("medium" as const)
                : ("high" as const),
            avito_id: String(item.avito_id || ""),
            title: String(item.item_title || ""),
            detected_at: String(item.detected_at || ""),
            description: String(
              item.reason ||
                item.current?.decision_reason?.text ||
                "Бидер быстро изменил направление ставки.",
            ),
            reason: String(item.reason || ""),
            account_name: String(item.account_name || ""),
            previous: item.previous || null,
            current: item.current || null,
            occurrences: 1,
            event_id: item.event_id ?? null,
          }),
        );
        const byAd = Object.fromEntries(
          Object.entries(raw?.by_ad || {}).map(([key, values]) => [
            key,
            (Array.isArray(values) ? values : []).map(
              (item: any, index: number) => ({
                id: `operational-${item.event_id ?? index}`,
                type: String(item.type || "direction_reversal"),
                severity:
                  item.severity === "medium"
                    ? ("medium" as const)
                    : ("high" as const),
                avito_id: String(item.avito_id || ""),
                title: String(item.item_title || ""),
                detected_at: String(item.detected_at || ""),
                description: String(
                  item.reason || item.current?.decision_reason?.text || "",
                ),
                reason: String(item.reason || ""),
                account_name: String(item.account_name || ""),
                previous: item.previous || null,
                current: item.current || null,
                occurrences: 1,
                event_id: item.event_id ?? null,
              }),
            ),
          ]),
        );
        return {
          items,
          total: items.length,
          by_ad: byAd,
          critical_count: Number(raw?.critical_count || 0),
          attention_count: Number(raw?.attention_count || items.length),
          as_of: raw?.as_of ?? null,
        };
      },
      providesTags: ["Monitoring"],
    }),

    getMonitoringBidderAnalyticsDataQuality: builder.query<
      BidderDataQuality,
      BidderEffectivenessFilters
    >({
      query: (filters) => ({
        url: "/monitoring/bidder/analytics/data-quality",
        params: bidderAnalyticsParams(filters),
      }),
      transformResponse: (raw: any): BidderDataQuality => ({
        coverage_pct:
          raw?.valid_rate == null ? null : Number(raw.valid_rate) * 100,
        measured_events: Number(raw?.reliable_events || 0),
        insufficient_events: Number(raw?.insufficient_events || 0),
        confounded_events: Number(raw?.confounded_events || 0),
        last_sample_at: null,
        issues: Array.isArray(raw?.issues) ? raw.issues.map(String) : [],
      }),
      providesTags: ["Monitoring"],
    }),

    rollbackMonitoringBidderRun: builder.mutation<
      {
        account: string;
        catalog: "iphone" | "samsung" | "xiaomi";
        run_id: number;
        reverted: number;
        skipped: number;
        errors: number;
      },
      { account: string; catalog?: "iphone" | "samsung" | "xiaomi" }
    >({
      query: ({ account, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/rollback/${encodeURIComponent(account)}`,
        method: "POST",
        params: { catalog },
      }),
      invalidatesTags: [
        "Monitoring",
        { type: "Monitoring", id: "today-summary" },
      ],
    }),

    setMonitoringBidderEnabled: builder.mutation<
      {
        account_name: string;
        catalog: "iphone" | "samsung" | "xiaomi";
        enabled: boolean;
      },
      {
        account: string;
        enabled: boolean;
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ account, enabled, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/enabled/${encodeURIComponent(account)}`,
        method: "POST",
        body: { enabled, catalog },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    stopAllMonitoringBidder: builder.mutation<
      {
        stopped: number;
        already_off: number;
      },
      void
    >({
      query: () => ({
        url: "/monitoring/bidder/stop-all",
        method: "POST",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    bulkApplyMonitoringBidderTemplate: builder.mutation<
      {
        template_id: number;
        applied: string[];
        failed: Array<{ account: string; error: string }>;
      },
      {
        template_id: number;
        accounts: string[];
        catalog?: "iphone" | "samsung" | "xiaomi";
      }
    >({
      query: ({ template_id, accounts, catalog = "iphone" }) => ({
        url: `/monitoring/bidder/templates/${template_id}/bulk-apply`,
        method: "POST",
        body: { accounts, catalog },
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getXLRotationStatuses: builder.query<
      {
        account_name: string;
        rotations: XLRotationListItem[];
      },
      { account: string }
    >({
      query: ({ account }) => ({
        url: `/monitoring/bidder/xl-rotation/${encodeURIComponent(account)}`,
      }),
      providesTags: ["Monitoring"],
    }),

    cancelXLRotation: builder.mutation<
      {
        account_name: string;
        model_key: string;
        restored: number;
        status: string;
      },
      { account: string; model_key: string }
    >({
      query: ({ account, model_key }) => ({
        url: `/monitoring/bidder/xl-rotation/${encodeURIComponent(account)}/cancel/${encodeURIComponent(model_key)}`,
        method: "POST",
      }),
      invalidatesTags: ["Monitoring"],
    }),

    triggerXLRotationManual: builder.mutation<
      XLRotationTriggerResponse,
      {
        account: string;
        source_xl_avito_id: string;
        click_reduction_percent: number;
        limit_reduction_percent: number;
        max_iterations: number;
      }
    >({
      query: ({ account, ...body }) => ({
        url: `/monitoring/bidder/xl-rotation/${encodeURIComponent(account)}/manual-trigger`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    triggerXLRotationManualBatch: builder.mutation<
      {
        account_name: string;
        items: Array<{
          source_xl_avito_id: string;
          model_key?: string | null;
          rotation_id?: string | null;
          status: string;
          detail?: string | null;
        }>;
      },
      {
        account: string;
        source_xl_avito_ids: string[];
        click_reduction_percent: number;
        limit_reduction_percent: number;
        max_iterations: number;
      }
    >({
      query: ({ account, ...body }) => ({
        url: `/monitoring/bidder/xl-rotation/${encodeURIComponent(account)}/manual-trigger-batch`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    getXLRotationDetail: builder.query<
      XLRotationDetail,
      { rotation_id: string }
    >({
      query: ({ rotation_id }) => ({
        url: `/monitoring/bidder/xl-rotation/rotation/${encodeURIComponent(rotation_id)}`,
      }),
      providesTags: ["Monitoring"],
    }),

    rollbackXLRotation: builder.mutation<
      XLRotationRollbackResponse,
      {
        rotation_id: string;
        force?: boolean;
      }
    >({
      query: ({ rotation_id, ...params }) => ({
        url: `/monitoring/bidder/xl-rotation/rotation/${encodeURIComponent(rotation_id)}/rollback`,
        method: "POST",
        body: params,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    restartXLRotationManual: builder.mutation<
      XLRotationRestartResponse,
      {
        account: string;
        model_key: string;
        body?: XLRotationRestartRequest;
      }
    >({
      query: ({ account, model_key, body = {} }) => ({
        url: `/monitoring/bidder/xl-rotation/${encodeURIComponent(account)}/restart/${encodeURIComponent(model_key)}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Monitoring"],
    }),

    // ==================== TASK MANAGER ====================

    getTaskManagerWorkspace: builder.query<TaskManagerWorkspace, void>({
      query: () => "/task-manager/workspace",
      providesTags: ["TaskManager"],
    }),

    getTaskManagerTasks: builder.query<TaskManagerTask[], number>({
      query: (listId) => `/task-manager/tasks/${listId}`,
      providesTags: ["TaskManager"],
    }),

    getTaskManagerTask: builder.query<TaskManagerTask, number>({
      query: (taskId) => `/task-manager/task/${taskId}`,
      providesTags: ["TaskManager"],
    }),

    createTaskManagerTask: builder.mutation<
      TaskManagerTask,
      {
        list_id: number;
        name: string;
        description?: string;
        priority?: string;
        source?: string;
        raw_text?: string;
      }
    >({
      query: (body) => ({
        url: "/task-manager/tasks",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    updateTaskManagerTask: builder.mutation<
      TaskManagerTask,
      { task_id: number; [key: string]: any }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/task-manager/task/${task_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    moveTaskManagerTask: builder.mutation<
      any,
      { task_id: number; status_id: number }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/task-manager/task/${task_id}/move`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    deleteTaskManagerTask: builder.mutation<{ ok: boolean }, number>({
      query: (taskId) => ({
        url: `/task-manager/task/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TaskManager"],
    }),

    analyzeTaskManagerTask: builder.mutation<
      TaskManagerAIAnalysis,
      TaskAnalyzeRequest
    >({
      query: (body) => ({
        url: "/task-manager/analyze",
        method: "POST",
        body,
      }),
    }),

    analyzeAndSaveTaskManagerTask: builder.mutation<
      { task: TaskManagerTask; analysis: TaskManagerAIAnalysis },
      number
    >({
      query: (taskId) => ({
        url: `/task-manager/analyze-and-save/${taskId}`,
        method: "POST",
      }),
      invalidatesTags: ["TaskManager"],
    }),

    getTaskManagerComments: builder.query<TaskManagerComment[], number>({
      query: (taskId) => `/task-manager/task/${taskId}/comments`,
      providesTags: ["TaskManager"],
    }),

    addTaskManagerComment: builder.mutation<
      TaskManagerComment,
      { task_id: number; content: string }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/task-manager/task/${task_id}/comments`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    getTaskManagerChecklists: builder.query<TaskManagerChecklist[], number>({
      query: (taskId) => `/task-manager/task/${taskId}/checklists`,
      providesTags: ["TaskManager"],
    }),

    addTaskManagerChecklist: builder.mutation<
      TaskManagerChecklist,
      { task_id: number; name: string }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/task-manager/task/${task_id}/checklists`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    addTaskManagerChecklistItem: builder.mutation<
      TaskManagerChecklistItem,
      { checklist_id: number; content: string }
    >({
      query: ({ checklist_id, ...body }) => ({
        url: `/task-manager/checklist/${checklist_id}/items`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    toggleTaskManagerChecklistItem: builder.mutation<
      TaskManagerChecklistItem,
      { item_id: number; is_checked: boolean }
    >({
      query: ({ item_id, ...body }) => ({
        url: `/task-manager/checklist-item/${item_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    getTaskManagerTags: builder.query<TaskManagerTag[], number>({
      query: (spaceId) => `/task-manager/tags/${spaceId}`,
      providesTags: ["TaskManager"],
    }),

    createTaskManagerTag: builder.mutation<
      TaskManagerTag,
      { space_id: number; name: string; color?: string }
    >({
      query: (body) => ({
        url: "/task-manager/tags",
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    toggleTaskManagerFavorite: builder.mutation<
      { is_favorite: boolean },
      number
    >({
      query: (taskId) => ({
        url: `/task-manager/task/${taskId}/favorite`,
        method: "POST",
      }),
      invalidatesTags: ["TaskManager"],
    }),

    getTaskManagerFavorites: builder.query<TaskManagerTask[], void>({
      query: () => "/task-manager/favorites",
      providesTags: ["TaskManager"],
    }),

    getTaskManagerActivity: builder.query<
      TaskManagerActivityLog[],
      { task_id: number; limit?: number }
    >({
      query: ({ task_id, limit }) =>
        `/task-manager/task/${task_id}/activity?limit=${limit || 50}`,
      providesTags: ["TaskManager"],
    }),

    bootstrapTaskManagerAssistant: builder.mutation<
      TaskAssistantBootstrapResponse,
      { task_id: number; reset?: boolean; model?: string }
    >({
      query: ({ task_id, ...body }) => ({
        url: `/task-manager/task/${task_id}/assistant-bootstrap`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["TaskManager"],
    }),

    // ==================== MONITORING RUNNER (24/7 positions) ====================
    getMonitoringRunnerAccounts: builder.query<
      { accounts: any[]; count: number },
      void
    >({
      query: () => "/monitoring/runner/accounts",
      providesTags: ["MonitoringRunner"],
    }),
    updateMonitoringRunnerAccounts: builder.mutation<
      any,
      { selections: { account_name: string; enabled: boolean }[] }
    >({
      query: (body) => ({
        url: "/monitoring/runner/accounts",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    createMonitoringRunnerExternalAccount: builder.mutation<any, { account_name: string; client_id: string; client_secret: string; timezone?: string; work_mode?: string }>({
      query: (body) => ({ url: "/monitoring/runner/external-accounts", method: "POST", body }),
      invalidatesTags: ["MonitoringRunner", "Monitoring"],
    }),
    getMonitoringRunnerCustomLinks: builder.query<any, { account_name?: string } | void>({
      query: (params) => ({ url: "/monitoring/runner/custom-links", params: params || undefined }),
      providesTags: ["MonitoringRunner"],
    }),
    createMonitoringRunnerCustomLink: builder.mutation<any, { account_name: string; url: string; avito_ids: string[] }>({
      query: (body) => ({ url: "/monitoring/runner/custom-links", method: "POST", body }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    createMonitoringRunnerCustomLinksBatch: builder.mutation<any, { account_name: string; links: Array<{ url: string; avito_ids: string[] }> }>({
      query: (body) => ({ url: "/monitoring/runner/custom-links/batch", method: "POST", body }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    deleteMonitoringRunnerCustomLink: builder.mutation<any, { link_id: string }>({
      query: ({ link_id }) => ({ url: `/monitoring/runner/custom-links/${encodeURIComponent(link_id)}`, method: "DELETE" }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    addMonitoringRunnerCustomLinkAds: builder.mutation<any, { link_id: string; avito_ids: string[] }>({
      query: ({ link_id, avito_ids }) => ({ url: `/monitoring/runner/custom-links/${encodeURIComponent(link_id)}/ads`, method: "POST", body: { avito_ids } }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    deleteMonitoringRunnerCustomLinkAd: builder.mutation<any, { link_id: string; avito_id: string }>({
      query: ({ link_id, avito_id }) => ({ url: `/monitoring/runner/custom-links/${encodeURIComponent(link_id)}/ads/${encodeURIComponent(avito_id)}`, method: "DELETE" }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerStatus: builder.query<any, void>({
      query: () => "/monitoring/runner/status",
      providesTags: ["MonitoringRunner"],
    }),
    startMonitoringRunner: builder.mutation<any, void>({
      query: () => ({ url: "/monitoring/runner/start", method: "POST" }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    stopMonitoringRunner: builder.mutation<any, void>({
      query: () => ({ url: "/monitoring/runner/stop", method: "POST" }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerPositions: builder.query<any, { days?: number } | void>({
      query: (params) => {
        const days = params && "days" in params ? params.days : 7;
        return `/monitoring/runner/positions?days=${days}`;
      },
      providesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerHistory: builder.query<
      any,
      { limit?: number; offset?: number } | void
    >({
      query: (params) => {
        const limit = params && "limit" in params ? params.limit : 50;
        const offset = params && "offset" in params ? params.offset : 0;
        return `/monitoring/runner/history?limit=${limit}&offset=${offset}`;
      },
      providesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerHistoryLog: builder.query<any, string>({
      query: (iterationId) => `/monitoring/runner/history/${iterationId}/log`,
    }),
    getMonitoringRunnerHealth: builder.query<any, void>({
      query: () => "/monitoring/runner/health",
      providesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerUrlLog: builder.query<any, string>({
      query: (url) =>
        `/monitoring/runner/url-log?url=${encodeURIComponent(url)}`,
    }),
    getMonitoringRunnerProxies: builder.query<any, void>({
      query: () => "/monitoring/runner/proxies",
      providesTags: ["MonitoringRunner"],
    }),
    assignMonitoringRunnerProxy: builder.mutation<
      any,
      { proxy_id: number; monitoring_only: boolean }
    >({
      query: (body) => ({
        url: "/monitoring/runner/proxies/assign",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    getMonitoringRunnerConfig: builder.query<any, void>({
      query: () => "/monitoring/runner/config",
      providesTags: ["MonitoringRunner"],
    }),
    updateMonitoringRunnerConfig: builder.mutation<any, Record<string, any>>({
      query: (body) => ({
        url: "/monitoring/runner/config",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MonitoringRunner"],
    }),
    testMonitoringRunnerTelegram: builder.mutation<
      any,
      { telegram_notify_chat_ids?: number[] } | void
    >({
      query: (body) => ({
        url: "/monitoring/runner/config/test-telegram",
        method: "POST",
        body,
      }),
    }),

    sendRealtimeScreenshots: builder.mutation<
      { sent_chats: number; total_photos: number; errors: string[] },
      { blob: Blob; accountName: string }[]
    >({
      query: (items) => {
        const formData = new FormData();
        items.forEach(({ blob, accountName }) => {
          formData.append("files", blob, `${accountName}.png`);
          formData.append("account_names", accountName);
        });
        return {
          url: "/analytics/realtime/send-screenshots",
          method: "POST",
          body: formData,
        };
      },
    }),
    getRealtimeClientBotCoverage: builder.query<
      { account_names: string[] },
      void
    >({
      query: () => "/analytics/realtime/client-bot-coverage",
    }),
    sendInternalRealtimeScreenshots: builder.mutation<
      {
        sent_chats: number;
        total_photos: number;
        excluded_client_accounts: string[];
        errors: string[];
      },
      { blob: Blob; accountName: string }[]
    >({
      query: (items) => {
        const formData = new FormData();
        items.forEach(({ blob, accountName }) => {
          formData.append("files", blob, `${accountName}.png`);
          formData.append("account_names", accountName);
        });
        return {
          url: "/analytics/realtime/send-internal-screenshots",
          method: "POST",
          body: formData,
        };
      },
    }),
  }),
});

export const {
  useGetAccountsQuery,
  useGetAutoloadAccountsQuery,
  useGetDashboardSummaryQuery,
  useGetSpendsChartQuery,
  useGetCTRChartQuery,
  useGetTopModelsQuery,
  useGetSpendsByAccountsQuery,
  useGetCampaignDetailsQuery,
  useGetAdvertisementsTableQuery,
  useGetSheetsFileInfoQuery,
  useSyncSheetsDataMutation,
  useAutoSyncSheetsMutation,
  useGetAccountsPerformanceQuery,
  useAskAIMutation,
  useHelpAIMutation,
  useAnalyzeTableMutation,
  useGetSummaryAssistantSettingsQuery,
  useUpdateSummaryAssistantSettingsMutation,
  useRunSummaryAssistantMutation,
  useListAgentsQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useListAgentThreadsQuery,
  useCreateAgentThreadMutation,
  useGetAgentThreadQuery,
  useDeleteAgentThreadMutation,
  useSendAgentMessageMutation,
  useOpenclawAgentMutation,
  useListOpenclawThreadsQuery,
  useCreateOpenclawThreadMutation,
  useGetOpenclawThreadQuery,
  useDeleteOpenclawThreadMutation,
  useSendOpenclawThreadMessageMutation,
  useStartOpenclawThreadMessageJobMutation,
  useLazyGetOpenclawThreadMessageJobStatusQuery,
  useLazyGetOpenclawThreadActiveRunQuery,
  useCancelOpenclawThreadMessageJobMutation,
  useListOpenclawRuntimeRunsQuery,
  useCancelAllOpenclawRuntimeRunsMutation,
  useCleanupStaleOpenclawRuntimeRunsMutation,
  useOpenclawPingQuery,
  useLazyOpenclawPingQuery,
  useGetOpenCodeModelPresetsQuery,
  useUpdateOpenCodeModelPresetsMutation,
  useGetOpenclawMemoryQuery,
  useUpdateOpenclawMemoryMutation,
  useOpenclawSkillsListMutation,
  useOpenclawSkillToggleMutation,
  useClawhubSearchMutation,
  useClawhubInstallMutation,
  useOpenclawSystemStatusMutation,
  useOpenclawSystemLogsMutation,
  useOpenclawSystemRestartMutation,
  useOpenclawModelsListMutation,
  useOpenclawModelsSetMutation,
  useGetAccountCabinetAccountsQuery,
  useGetAccountCabinetQuery,
  useGetAccountCabinetParserRunQuery,
  useGetAccountCabinetCommentsQuery,
  useCreateAccountCabinetCommentMutation,
  useUpdateAccountCabinetChecklistMutation,
  useImportAccountCabinetChecklistMutation,
  useGetAccountCabinetMetricHighlightSettingsQuery,
    useUpdateAccountCabinetMetricHighlightSettingsMutation,
    useGetAccountCabinetScoreSettingsQuery,
    useUpdateAccountCabinetScoreSettingsMutation,
    useImportAccountCabinetScoreSettingsMutation,
  useGetRealtimeSummaryQuery,
  useLazyGetRealtimeBalancesQuery,
  useListAnalyticsSummaryTasksQuery,
  useGetBlockedItemsQuery,
  useLazyGetBlockedItemsQuery,
  useGetRealtimeXlMetricsQuery,
  useGetYesterdaySpendsQuery,
  useGetFeedbackQuery,
  useSyncFeedbackMutation,
  useGetFeedbackSettingsQuery,
  useUpdateFeedbackSettingsMutation,
  useDisputeReviewMutation,
  useAddNoteMutation,
  useMarkReviewAsRepliedMutation,
  useGetChatByReviewMutation,
  useGenerateDisputeTextMutation,
  useSaveReviewAiDisputeDraftMutation,
  useDeleteReviewAiDisputeDraftMutation,
  useSendReviewForApprovalMutation,
  useGenerateReviewResponseMutation,
  useGetAutoloadReportsQuery,
  useGetAutoloadUserDocsTreeQuery,
  useGetAutoloadUserDocsFieldsQuery,
  useLazyGetAutoloadUserDocsFieldsQuery,
  useGetAutoloadUserDocsValuesQuery,
  useGetAutoloadReportByIdQuery,
  useGetAutoloadReportItemsQuery,
  useLazyGetAutoloadReportItemsQuery,
  useUpdateSubscriptionDateMutation,
  useRefreshAccountSubscriptionSourceMutation,
  useUpdateTariffMutation,
  useUpdateAnalyticsMutation,
  useGetAnalyticsUpdateStatusQuery,
  useGetHiddenAdsComparisonQuery,
  useRefreshHiddenAdsComparisonMutation,
  useGetHiddenAdsComparisonProgressQuery,
  useGetHiddenAdsActiveAccountsQuery,
  useGetHiddenAdsDetailsQuery,
  useCollectAllAccountsMutation,
  useGetDataStatusQuery,
  useCreateAnalyticsSummaryMutation,
  useGetAnalyticsSummariesQuery,
  useDeleteAnalyticsSummaryMutation,
  useDeleteAllAnalyticsSummariesMutation,
  useGetAnalyticsSummaryTaskQuery,
  useCancelAnalyticsSummaryTaskMutation,
  useCreateSummaryTableBatchMutation,
  useDismissAnalyticsSummaryTaskMutation,
  useGetMonitoringConfigQuery,
  useGetServiceLevelAccountsQuery,
  useGetServiceLevelTaskQuery,
  useSetMonitoringConfigMutation,
  useFetchAvtoolsCsvMutation,
  useRefreshMonitoringPositionsCacheMutation,
  useGetMonitoringDisableStatusConfigQuery,
  useSetMonitoringDisableStatusConfigMutation,
  useRunMonitoringDisableStatusCheckMutation,
  useApplyMonitoringDisableStatusPendingActionMutation,
  useRunMonitoringPlanningScanMutation,
  useGetMonitoringPlanningRecommendationsMutation,
  useRunServiceLevelCollectionMutation,
  useGetServiceLevelAccountSettingsQuery,
  useGetServiceLevelProfileGroupBindingsQuery,
  useUpdateServiceLevelProfileGroupBindingsMutation,
  useUpsertServiceLevelAccountSettingsMutation,
  useGetLastServiceLevelTaskQuery,
  useUploadServiceLevelToSheetsMutation,
  useApplyMonitoringPlanningUpdatesMutation,
  useGetMonitoringPlanningPricesMutation,
  useListServiceLevelTasksQuery,
  useCancelServiceLevelTaskMutation,
  useGetServiceLevelScheduleQuery,
  useUpdateServiceLevelScheduleMutation,
  useGetServiceLevelHistoryQuery,
  useWriteServiceLevelHistoryToReportMutation,
  useGetMonitoringAccountsQuery,
  useGetMonitoringViewQuery,
  useGetMonitoringWorkspaceSnapshotQuery,
  useGetMonitoringSpendGuardQuery,
  usePutMonitoringSpendPolicyMutation,
  useDeleteMonitoringSpendPolicyMutation,
  useGetMonitoringWorkspaceManifestQuery,
  useGetMonitoringCatalogViewQuery,
  useGetMonitoringExtraProductsOverviewQuery,
  useGetMonitoringExtraProductSourcesQuery,
  useSaveMonitoringExtraProductSourceMutation,
  useDeleteMonitoringExtraProductSourceMutation,
  useRunMonitoringExtraProductsSyncMutation,
  useGetMonitoringCatalogConfigQuery,
  useUpdateMonitoringCatalogConfigMutation,
  useRunMonitoringCatalogSyncMutation,
  useGetMonitoringMetricsQuery,
  useGetMonitoringPriceHistoryQuery,
  useGetMonitoringPriceDynamicsGroupsQuery,
  useLazyGetMonitoringPriceDynamicsGroupAdsQuery,
  useGetMonitoringReportSummaryQuery,
    useGetMonitoringCategoriesQuery,
    useGetMonitoringAccountCommentQuery,
    useGetMonitoringAccountCommentsQuery,
    useSaveMonitoringAccountCommentMutation,
  useGetMonitoringClickLimitQuery,
  useGetMonitoringPromotionSyncRequestQuery,
  useRequestMonitoringPromotionSyncMutation,
  useGetMonitoringMassPostingSnapshotQuery,
  useGetMonitoringCatalogClickLimitQuery,
  useUpdateMonitoringCatalogClickLimitMutation,
  useUpdateMonitoringCatalogClickLimitBulkMutation,
  useLazyGetMonitoringMassPostingPreviewQuery,
  useSaveMonitoringMassPostingMutation,
  useUpdateMonitoringMassPostingClickLimitMutation,
  useUpdateMonitoringMassPostingClickLimitBulkMutation,
  useGetMonitoringMassPostingPromotionSpaceQuery,
  useLazyGetMonitoringMassPostingPromotionSpaceQuery,
  useApplyMonitoringMassPostingPromotionSpaceMutation,
  useGetMonitoringCpxSnapshotQuery,
  useGetMonitoringCpxAccountSummaryQuery,
  useUpdateMonitoringClickLimitBulkMutation,
  useUpdateMonitoringClickLimitMutation,
  useRunAutoAZMutation,
  useGetAutoAZTaskStatusQuery,
  useCancelAutoAZMutation,
  useLazyGetSheetDescriptionsQuery,
  useLazyGetSheetImageUrlsQuery,
  useAddIphoneModelMutation,
  useWriteAnalyticsBlockMutation,
  useGetMonitoringActionHistoryQuery,
  useLazyGetMonitoringActionHistoryQuery,
  useGetMonitoringActionHistorySummaryQuery,
  useGetMonitoringActionHistoryTodaySummaryQuery,
  useLazyGetMonitoringActionHistoryLatestByAdQuery,
  useGetMonitoringPromotionDailySummaryQuery,
  useLazyGetMonitoringPromotionDayAdsDetailQuery,
  useGetMonitoringPlanningConfigQuery,
  useSetMonitoringPlanningConfigMutation,
  useGetMonitoringAccountRatingQuery,
  useCreateSummaryTableMutation,
  useGetSummaryTableDataQuery,
  useUpdateSummaryManualFieldsMutation,
  useUpdateSummaryManualCellHighlightMutation,
  useClearSummaryManualCellHighlightsMutation,
  useGetSavedSummariesQuery,
  useLazyGetSavedSummaryQuery,
  useDeleteSavedSummaryMutation,
  useCreateSummaryShareMutation,
  useGetSummarySharesQuery,
  useDeleteSummaryShareMutation,
  useTransferFundsMutation,
  useGetTaskManagerWorkspaceQuery,
  useGetTaskManagerTasksQuery,
  useGetTaskManagerTaskQuery,
  useCreateTaskManagerTaskMutation,
  useUpdateTaskManagerTaskMutation,
  useMoveTaskManagerTaskMutation,
  useDeleteTaskManagerTaskMutation,
  useAnalyzeTaskManagerTaskMutation,
  useAnalyzeAndSaveTaskManagerTaskMutation,
  useGetTaskManagerCommentsQuery,
  useAddTaskManagerCommentMutation,
  useGetTaskManagerChecklistsQuery,
  useAddTaskManagerChecklistMutation,
  useAddTaskManagerChecklistItemMutation,
  useToggleTaskManagerChecklistItemMutation,
  useGetTaskManagerTagsQuery,
  useCreateTaskManagerTagMutation,
  useToggleTaskManagerFavoriteMutation,
  useGetTaskManagerFavoritesQuery,
  useGetTaskManagerActivityQuery,
  useBootstrapTaskManagerAssistantMutation,
  useGetMonitoringRunnerAccountsQuery,
  useUpdateMonitoringRunnerAccountsMutation,
  useCreateMonitoringRunnerExternalAccountMutation,
  useGetMonitoringRunnerCustomLinksQuery,
  useCreateMonitoringRunnerCustomLinkMutation,
  useCreateMonitoringRunnerCustomLinksBatchMutation,
  useDeleteMonitoringRunnerCustomLinkMutation,
  useAddMonitoringRunnerCustomLinkAdsMutation,
  useDeleteMonitoringRunnerCustomLinkAdMutation,
  useGetMonitoringRunnerStatusQuery,
  useStartMonitoringRunnerMutation,
  useStopMonitoringRunnerMutation,
  useGetMonitoringRunnerPositionsQuery,
  useGetMonitoringRunnerHistoryQuery,
  useGetMonitoringRunnerHistoryLogQuery,
  useGetMonitoringRunnerHealthQuery,
  useGetMonitoringRunnerUrlLogQuery,
  useGetMonitoringRunnerProxiesQuery,
  useAssignMonitoringRunnerProxyMutation,
  useGetMonitoringRunnerConfigQuery,
  useUpdateMonitoringRunnerConfigMutation,
  useTestMonitoringRunnerTelegramMutation,
  useSendRealtimeScreenshotsMutation,
  useGetRealtimeClientBotCoverageQuery,
  useSendInternalRealtimeScreenshotsMutation,
  useGetMonitoringBidderConfigQuery,
  useUpdateMonitoringBidderConfigMutation,
  useGetMonitoringBidderTemplatesQuery,
  useCreateMonitoringBidderTemplateMutation,
  useDeleteMonitoringBidderTemplateMutation,
  useApplyMonitoringBidderTemplateMutation,
  useRunMonitoringBidderDryRunMutation,
  useGetMonitoringBidderAvailableDatesQuery,
  useLazyGetMonitoringBidderAvailableDatesQuery,
  useRunMonitoringBidderSimulateCompareMutation,
  useRunMonitoringBidderApplyMutation,
  useGetMonitoringBidderOverviewQuery,
  useGetMonitoringAccountWorkbenchQuery,
  useSaveMonitoringAccountWorkbenchMutation,
  useGetMonitoringPosition51SummaryQuery,
  useGetMonitoringPosition51ReplayQuery,
  useGetMonitoringBidderRunsQuery,
  useGetMonitoringBidderRunItemsQuery,
  useLazyGetMonitoringBidderRunItemsQuery,
  useGetMonitoringBidderAnalyticsSummaryQuery,
  useGetMonitoringBidderAnalyticsEventsQuery,
  useGetMonitoringBidderAnalyticsTimelineQuery,
  useGetMonitoringBidderAnalyticsRulesQuery,
  useGetMonitoringBidderAnalyticsAnomaliesQuery,
  useGetMonitoringBidderOperationalAnomaliesQuery,
  useGetMonitoringBidderAnalyticsDataQualityQuery,
  useRollbackMonitoringBidderRunMutation,
  useSetMonitoringBidderEnabledMutation,
  useStopAllMonitoringBidderMutation,
  useBulkApplyMonitoringBidderTemplateMutation,
  useGetXLRotationStatusesQuery,
  useCancelXLRotationMutation,
  useTriggerXLRotationManualMutation,
  useTriggerXLRotationManualBatchMutation,
  useGetXLRotationDetailQuery,
  useLazyGetXLRotationDetailQuery,
  useRollbackXLRotationMutation,
  useRestartXLRotationManualMutation,
} = analyticsApi;
